import { isEditableTarget } from "./_typing-target.js";
import { RuntimeTestStage } from "./stage.js";
import { createCartService, cartTransportFor, cartTransportKey, cartRefusal, orderStatusWord, formatPortMoney, checkoutStopperShown } from "./cart-service.js";
import { evaluateRefresh } from "./_live-refresh.js";   // M7 Chunk 3 / WS5 — live customer refresh (ASK-080 §E)
import { checkoutForcedClosed, planeWord } from "./_features.js";   // ASK-145 §2: production keeps checkout in catalog mode by flag
import { bookBinding } from "./_book-binding.js";   // ASK-139 F-139aj: `binding: left | right` on the feed row, default left
import { announce as loadingAnnounce, progress as loadingProgress, imageSkeleton } from "./_loading.js";
import { mountHudSafeZone } from "./_hud-safe-zone.js";
import { showToast } from "./_toast.js";   // ASK-163 4s item 1b: the toast dismisses itself and has a close   // ASK-163 4s item 1a: no HUD control covers an open sheet
import { makeTelemetryTransport } from "./_telemetry-transport.js";
import { isMerchantShopScene } from "./_shop-capability.js";
import { mayAuthorShop, operatorForeignShop } from "./_authority.js";   // ASK-142 F-142d: the Studio mounts only where the authority may author   // ASK-129 rerun-3: one hub-skip for every merchant-API surface
import { earlyJson } from "./_early-fetch.js";   // ASK-178 §3 item 2 (D-088 Slice 0): the registry read index.html started at parse
import { readBootSchema, describeBootSchema } from "./_boot-schema.js";   // ASK-131 §2b: refuse to hot-swap a boot we cannot read
import { parseDoorParams, resolveDeepLinkShop, resolveEntryMode, consoleUrlForShop, doorShopId, consoleOriginForPlane } from "./_cross-app-doors.js";   // ASK-103 slice E — the console↔Studio doors
import { foldShopKnobs } from "./_shop-knob-wire.js";
import { loadAudioPrefs, normaliseAudio } from "./_audio-prefs.js";   // ASK-174: ONE audio preference model, hub + shop   // ASK-129 F-102i: ONE storefront identity — the merchant's `.shop` layer wins, here as everywhere
// companions: bailey ─────────────────────────────────────────────────
// companions: end ────────────────────────────────────────────────────

const SETTINGS_STORAGE_KEY = "kapowx_settings";

// ASK-174 (Client 5d): the audio settings are `kapowx_settings.audio { master, ambient, cues }` (_audio-prefs.js) — the old
// masterVolume key migrates once; ambientEnabled / sfxEnabled are removed and never migrated (their stored false was the old
// default written by every save, not a choice — Client 5e).
const DEFAULT_SETTINGS = {};

export function bootstrapRuntimeTestApp(root, config) {
	// Read once at boot so any product normalize that fires during scene
	// composition sees the same flag. `?demo-interior=1` activates the
	// E-Open verb + stubs 6-page interior data on every product, lets the
	// page-flip animation be exercised in-browser without waiting for GCG
	// to author real interior images.
	if (typeof window !== "undefined") {
		const params = new URLSearchParams(window.location.search);
		window.__kapowxInteriorDemoMode = params.has("demo-interior");
	}
	return new RuntimeTestApp(root, config);
}

class RuntimeTestApp {
	constructor(root, config) {
		this.root = root;
		this.config = config;
		// Cart lives in its own service — see cart-service.js. The service
		// owns state.cart's contents; we mirror via onChange so existing
		// render code can keep reading from this.state.cart synchronously.
		this.cartService = createCartService(config);
		this.cartService.onChange((lines) => {
			this.state.cart = lines;
			this.renderCartCount();
			this.renderCartPanel();
		});

		// Paystack redirect-return (M6 money loop). Paystack sends the customer
		// back to the Callback URL with ?trxref=&reference=; if the active
		// transport holds a pending port-cart, complete it into an order and
		// confirm. Fire-and-forget — the webhook/backend stays source of truth
		// (DECISION-031 §3.3); this is the customer-facing confirmation beat.
		void this._handlePaystackReturn();

		// The demo-store scene comes from `config.shops["demo-store"]` (the minted boot). The Phase 0 WordPress
		// harness's `publishedScene` / `savedDraft` / `authoringPackage` (wp_localize_script) went with the harness
		// (ASK-159 §2.18ff, 4aa; archived under documentation-archive/phase-0-wordpress/).
		const demoStoreScene = config.shops?.["demo-store"]?.scene || null;
		this.state = {
			scene: demoStoreScene,
			package: null,
			sceneSource: demoStoreScene ? "shops" : "empty",
			// Currently-active shop's product list (fed into scene composition).
			// Populated by `_fetchProductsForShop` on portal entry; empty while
			// in the hub or while a fetch is in flight.
			products: [],
			// Per-shop product cache, keyed by projectId. Avoids refetch on
			// hub→shop→hub→same-shop hops. Populated lazily by
			// `_fetchProductsForShop`. Persistent until session ends.
			productsByShop: {},
			// ASK-097 Step 2 — per-shop RESOLVED assortment pool (feed `assortment[]`; each row carries
			// `seats[]`). Read by the Studio STOCK Inspector + assortment browser so the panel reflects
			// exactly what the pixels seat from. Keyed + cached like productsByShop.
			assortmentByShop: {},
			selectedComic: null,
			cart: this.cartService.lines,
			isLoading: false,
			cartOpen: false,
			settingsOpen: false,
			settings: this.loadSettings(),
			heroPanelOpen: false,
			heroPanelCoverSlot: 'front',
			// Per-page-load UUID for telemetry attribution (DECISION-012,
			// ASK-002). Re-rolled on every fresh app construction.
			telemetrySessionId: (typeof crypto !== "undefined" && crypto.randomUUID)
				? crypto.randomUUID()
				: `s-${Date.now()}-${Math.random().toString(36).slice(2)}`,
			// Stash boot-start so perf:page-load can measure end-to-end.
			bootStartMs: Date.now(),
		};

		this.root.innerHTML = this.renderShell();
		this.refs = this.getRefs();
		try { mountHudSafeZone(); } catch { /* no DOM (node suites) */ }
		this.stage = new RuntimeTestStage({
			mount: this.refs.stage,
			onComicSelect: (payload) => {
				const wasHolding = !!this.state.selectedComic?.product;
				this.state.selectedComic = payload;
				this.renderComicHud();
				// D-076 cl.5 — FOCUS MODE while something is held: the shop dims, the crosshair hides
				// (CSS on this class), the pointer is the panel's. Putting it back resumes the walk.
				this.root.classList.toggle("is-inspecting", !!payload?.product);
				if (!payload?.product && wasHolding) this._resumeWalk();
				// AAA inspect-modal (Shaun 2026-06-12): examining an item is a UI
				// state — free the cursor the moment a product engages so the hero
				// panel (Add to Cart) is immediately clickable. Esc then has ONE
				// meaning while inspecting: put it back. (RE4/Skyrim inspect +
				// Cyberpunk verb-keys pattern; F adds without touching the mouse.)
				if (payload?.product && document.pointerLockElement) {
					document.exitPointerLock?.();
				}
				// Telemetry-0 M5 C11: `product:interaction` business event —
				// fires on engage (payload non-null) only; disengage (null)
				// stays silent to avoid doubling each interaction. Captures
				// customer attention metric for merchant dashboard per M5 C12.
				if (payload?.product) {
					this._emitTelemetry("product:interaction", {
						shopId:      this.state.activeProjectId || null,
						productId:   payload.product.id || payload.product.kapowxProductId || null,
						productType: payload.product.kapowxProductType || payload.product.product_type || null,
						interactionKind: "engage",
					});
				}
			},
			onBoxHover: (box) => {
				this.updateBoxHint(box);
			},
			onHeroPosUpdate: (pos) => {
				this._updateComicHudPos(pos);
			},
			// D-086 (ASK-177 §10): every door — the street's box / disc / prompt, a shop's exit — goes through the entrance chunk
			// (acknowledge, push, the threshold card); a failed chunk load still walks in
			onPortalEnter: (trigger) => {
				import("./_hub-entrance.js").then((m) => m.enterThrough(this, trigger)).catch(() => this.navigateToProject(trigger.target, trigger.spawnRef));
			},
		});
		this.stage.onContentsChanged = () => this._refreshStageStatus();
		this.stage._bootConfig = () => this.config;   // ASK-156: the avatarCatalogue, read when the zoom first goes out   // F-139h: the status line follows the stage
		this.stage._avatarPref = () => this._avatarPickNow?.() || null;
		// ASK-174 — the audio model's two seams for the lazy chunks (Settings → Audio writes, the engine reads)
		this.stage.readAudioPrefs = () => normaliseAudio(this.state.settings.audio);
		this.stage.writeAudioPrefs = (next) => this.setAudioPrefs(next);
		this.stage.setAudioPrefs(this.state.settings.audio);   // ASK-167 step 4 — the pick (lazy `_social.js`; null = the default until it loads)

		// Shop scenes get their spawn + exit from the shell GLB's SPAWN_*
		// and EXIT_ANCHOR empties — no app-side injection needed. See
		// `_collectShopAnchors` in stage.js for the contract.

		// Map of project id → bootstrap. navigateToProject(id, spawnRef) routes here.
		// Reads config.shops (the canonical multi-tenant surface emitted by
		// build-boot-json.mjs); falls back to legacy top-level keys for any
		// cached bundle that's still on the old shape. Adding a new shop is
		// pure data — drop a project.json, run npm run boot.
		this.projectBootstraps = this._assembleProjectBootstraps(config);

		this.bindEvents();
		// ASK-139 F-139c — the alpha customer chrome: Report (every walker), Credits (from the menu),
		// and the touch first-run hint. The pill is in the shell; each SHEET is a lazy chunk mounted on
		// first use — none of them is first-frame work, and the customer bundle budget says so.
		this._sheetHost = this.root.querySelector(".kapowx-store") || this.root;
		// The bottom sheet's drag-down (Put back) is a touch gesture — it rides the touch-only chunk.
		try {
			if (window.matchMedia?.("(any-pointer: coarse)").matches) {
				import("./_touch-walk.js").then((m) => m.installSheetSwipe(this.refs.heroPanel, () => this.stage?.exitHero?.())).catch(() => {});
			}
		} catch { /* no matchMedia */ }
		this._report = lazySheet(() => import("./_report-sheet.js").then((m) => m.mountReport(this, this._sheetHost, this.refs.reportPill)));
		this._credits = lazySheet(() => import("./_credits-sheet.js").then((m) => m.mountCredits(this, this._sheetHost)));
		this._touchHint = lazySheet(() => import("./_touch-hint.js").then((m) => m.mountTouchHint(this, this._sheetHost)));
		// The cart backend is a SHOP fact (feed `commerce`); until a shop says otherwise the boot default
		// serves, and `_bindShopCommerce` rebinds on entry.
		this._cartTransportKey = "default";
		this._cartHydrated = false;
		// D-076 cl.2 — tell the stage how much of the canvas the product panel covers, so it frames the
		// held object in what is LEFT. Measured off the panel itself (it is laid out even while closed),
		// so the breakpoint and the sheet sizes live in one place: the stylesheet.
		this._syncInspectInset();
		window.addEventListener("resize", () => this._syncInspectInset());
		this.renderCartCount();
		this._installCapacityOverflowRelay();
		this._installRuntimeErrorRelay();
		this.bootstrap();
		// Defer settings apply until after first user gesture triggers audio
		setTimeout(() => this.applySettings(), 500);
		// Show the "Click to play" prompt — pointer-lock isn't available yet
		// (browser security needs a user gesture). Hides via pointerlockchange
		// once the player clicks the canvas. Also re-shown after settings close
		// if the lock-request was rejected (Chrome 1.5s cooldown).
		this.updateEngagePromptVisibility();
		// Expose for console-driven testing of scene hops (Stage 1+).
		// Safe to leak: one app per page and mutation-only via documented methods.
		window.__kapowxApp = this;
	}

	getRefs() {
		return {
			stage:        this.root.querySelector("[data-runtime-stage]"),
			stageStatus:  this.root.querySelector("[data-runtime-stage-status]"),
			loader:       this.root.querySelector("[data-runtime-loader]"),
			boxHint:      this.root.querySelector("[data-runtime-box-hint]"),
			boxHintName:  this.root.querySelector("[data-runtime-box-hint-name]"),
			comicConnector: this.root.querySelector("[data-runtime-comic-connector]"),
			comicHud:     this.root.querySelector("[data-runtime-comic-hud]"),
			hudTitle:     this.root.querySelector("[data-runtime-hud-title]"),
			hudPrice:     this.root.querySelector("[data-runtime-hud-price]"),
			comicActions: this.root.querySelector("[data-runtime-comic-actions]"),
			cartPanel:    this.root.querySelector("[data-runtime-cart-panel]"),
			cartItems:    this.root.querySelector("[data-runtime-cart-items]"),
			cartSummary:  this.root.querySelector("[data-runtime-cart-summary]"),
			cartCount:    this.root.querySelector("[data-runtime-cart-count]"),
			cartToggle:   this.root.querySelector("[data-runtime-cart-toggle]"),
			cartClose:    this.root.querySelector("[data-runtime-cart-close]"),
			cartTitle:    this.root.querySelector("[data-runtime-cart-title]"),
			reportPill:   this.root.querySelector("[data-runtime-report]"),
			checkoutMount: this.root.querySelector("[data-runtime-checkout-mount]"),
			empty:           this.root.querySelector("[data-runtime-empty]"),
			controlsHint:    this.root.querySelector("[data-runtime-controls-hint]"),
			settingsBtn:     this.root.querySelector("[data-runtime-settings-btn]"),
			settingsOverlay: this.root.querySelector("[data-runtime-settings-overlay]"),
			presenceCount:   this.root.querySelector("[data-runtime-presence-count]"),
			heroPanel:           this.root.querySelector("[data-hero-panel]"),
			heroPanelCover:      this.root.querySelector("[data-hero-panel-cover]"),
			heroPanelCoverEmpty: this.root.querySelector("[data-hero-panel-cover-empty]"),
			heroPanelThumbs:     this.root.querySelector("[data-hero-panel-thumbs]"),
			heroPanelTitle:      this.root.querySelector("[data-hero-panel-title]"),
			heroPanelBadges:     this.root.querySelector("[data-hero-panel-badges]"),
			heroPanelPrice:      this.root.querySelector("[data-hero-panel-price]"),
			heroPanelMaker:      this.root.querySelector("[data-hero-panel-maker]"),
			heroPanelScale:      this.root.querySelector("[data-hero-panel-scale]"),
			heroPanelActions:    this.root.querySelector("[data-hero-panel-actions]"),
			heroPanelHint:       this.root.querySelector("[data-hero-panel-hint]"),
			heroPanelCheckoutNote: this.root.querySelector("[data-hero-panel-checkout-note]"),
			heroPanelMedia3d:    this.root.querySelector("[data-hero-panel-media-3d]"),
			engagePrompt:        this.root.querySelector("[data-runtime-engage]"),
		};
	}

	// Show / hide the "Click to play" overlay based on whether the pointer
	// is currently locked. Called on every pointerlockchange and once on
	// initial mount (where the pointer starts unlocked because browser
	// security requires a user gesture to acquire lock).
	/** Free the cursor for a surface that takes the keys (the chat line) — NOT a pause: the pointerlockchange that follows
	 *  does not open Settings. (Esc is still the pause: it never comes through here.) */
	releasePointerFor(reason) {
		if (!document.pointerLockElement) return false;
		this._pointerReleaseFor = reason || "surface";
		document.exitPointerLock?.();
		return true;
	}

	updateEngagePromptVisibility() {
		if (!this.refs.engagePrompt) return;
		const locked      = !!document.pointerLockElement;
		const menuOpen    = this.state.settingsOpen;
		const heroActive  = !!this.stage?.activeFixtureHero;
		// ASK-139 F-139c — "click to play" is a pointer-lock affordance; a phone never locks, it walks.
		const touch       = this._touchFirst();
		this.refs.engagePrompt.hidden = locked || menuOpen || heroActive || touch;
		// AAA HUD ruling (ASK-087): no visible-but-unclickable buttons during lock. While the pointer
		// is captured the cursor can't reach floating chrome, so hide it; Esc releases the lock and
		// opens the pause/settings surface, which is where help + settings now live. Crosshair stays.
		for (const el of [this.refs.settingsBtn, this.refs.cartToggle, this.refs.reportPill]) {
			if (el) el.hidden = locked;
		}
		this._syncBasketChip();
		// ASK-172 §5.3 — the Invite pill follows the same rule (its own `hidden` says "not in a room", so lock is a class)
		this.root.querySelector("[data-runtime-invite]")?.classList.toggle("is-locked-hidden", locked);
		this.root.querySelector("[data-runtime-chat-tab]")?.classList.toggle("is-locked-hidden", locked);   // ASK-172 §8: Enter is the way in while playing
	}

	showControlsHint() {
		this.refs.controlsHint.classList.remove("is-hidden");
		clearTimeout(this._controlsHintTimer);
		this._controlsHintTimer = setTimeout(() => this.hideControlsHint(), 6000);
		if (this._controlsDismissHandler) {
			window.removeEventListener("keydown", this._controlsDismissHandler);
		}
		this._controlsDismissHandler = () => this.hideControlsHint();
		window.addEventListener("keydown", this._controlsDismissHandler, { once: true });
	}

	hideControlsHint() {
		this.refs.controlsHint.classList.add("is-hidden");
		clearTimeout(this._controlsHintTimer);
	}

	bindEvents() {
		// F-139d — the first finger on a fine-pointer device (a touch laptop) makes it touch-first from here.
		this.root.addEventListener("pointerdown", (e) => {
			if (e.pointerType !== "touch" || this._sawTouch) return;
			this._sawTouch = true;
			this.updateEngagePromptVisibility();
		}, true);
		// One panel at a time (ASK-159 §2.2d): a HUD control answers only a press that STARTED on it. Measured on
		// store-test-2, iPhone 15 landscape: a tap on a comic opens the side sheet, the HUD moves left of it, and the
		// same tap's compatibility click lands on the Report pill now under the finger — both sheets open from one tap.
		// The press is read on window (the canvas may sit outside root); keyboard/programmatic clicks (detail 0) pass.
		window.addEventListener("pointerdown", (e) => { this._pressTarget = e.target; }, { capture: true, passive: true });
		this.root.addEventListener("click", (event) => {
			const hud = event.target.closest?.("[data-runtime-report], [data-runtime-invite], [data-runtime-cart-toggle], [data-runtime-settings-btn]");
			if (hud && event.detail > 0 && this._pressTarget && !hud.contains(this._pressTarget)) return;
			if (event.target.closest("[data-runtime-settings-btn]")) {
				this.openSettings();
				return;
			}
			if (event.target.closest("[data-runtime-settings-close]")) {
				this.closeSettings();
				return;
			}
			if (event.target.closest("[data-runtime-settings-overlay]") === this.refs.settingsOverlay &&
				!event.target.closest("[data-runtime-settings-panel]")) {
				this.closeSettings();
				return;
			}
			if (event.target.closest("[data-avatar-kind], [data-avatar-tint]")) {
				const el = event.target.closest("[data-avatar-kind], [data-avatar-tint]");
				void this._social().then((m) => m.onAvatarChoice(this, el));
				return;
			}
			if (event.target.closest("[data-setting]")) {
				const el = event.target.closest("[data-setting]");
				this._handleSettingToggle(el);
				return;
			}
			if (event.target.closest("[data-runtime-cart-toggle]")) {
				this.state.cartOpen = !this.state.cartOpen;
				this.renderCartPanel();
				return;
			}
			if (event.target.closest("[data-runtime-cart-close]")) {
				this.state.cartOpen = false;
				this.renderCartPanel();
				return;
			}
			if (event.target.closest("[data-runtime-add-selected]")) {
				this.addSelectedComicToCart();
				return;
			}
			if (event.target.closest("[data-runtime-cart-remove]")) {   // card 123qy9rtpd9 — one line out (the line DELETE)
				void this.cartService.removeItem(event.target.closest("[data-runtime-cart-remove]").dataset.runtimeCartRemove);
				return;
			}
			if (event.target.closest("[data-runtime-clear-cart]")) {
				void this.cartService.clear();
				return;
			}
			if (event.target.closest("[data-runtime-cart-checkout]")) {
				void this.checkout();
				return;
			}
			if (event.target.closest("[data-runtime-my-orders]")) {   // K-COM item 2 — the shopper's orders in this shop
				this.state.cartOpen = false;
				this.renderCartPanel();
				void this._showMyOrders();
				return;
			}
			if (event.target.closest("[data-runtime-report]")) {
				this._report.open();
				return;
			}
			// ASK-172 §5.3 — the Invite pill: the party module (lazy, presence on) owns it; the tap is the user gesture the
			// share sheet needs, so it is handed over synchronously
			if (event.target.closest("[data-runtime-invite]")) {
				if (this.state.settingsOpen) this.closeSettings({ relock: false });   // reached from the pause (Esc) surface: a sheet opens next
				void this._partyClient?.onPill();
				return;
			}
			if (event.target.closest("[data-runtime-credits]")) {
				this.closeSettings({ relock: false });   // a sheet opens next — the cursor must reach it
				this._credits?.open();
				return;
			}
			if (event.target.closest("[data-runtime-touch-hint]")) {
				this.closeSettings({ relock: false });
				this._touchHint?.open();
				return;
			}
			if (event.target.closest("[data-runtime-signout]")) {
				// main.ts owns the actual Firebase signOut + reload; this just
				// triggers it. The settings overlay stays up until the reload
				// kicks in (~50ms) so there's no flash of confused UI.
				void this.config.onSignOut?.();
				return;
			}
			if (event.target.closest("[data-runtime-flip-comic]")) {
				this.stage.flipActiveComic?.();
				return;
			}
			if (event.target.closest("[data-runtime-close-comic]")) {
				this.stage.putHeroBack?.();
				this.stage.exitPileHero?.();
				this.stage.exitWallComic?.();
				return;
			}
			if (event.target.closest("[data-hero-panel-open]")) {
				this.stage.openInterior?.();
				return;
			}
			if (event.target.closest("[data-hero-panel-add]")) {
				this.addSelectedComicToCart();
				return;
			}
			if (event.target.closest("[data-hero-panel-flip]")) {
				this.stage.flipActiveComic?.();
				return;
			}
			// Put back and the panel's close are ONE verb (R/Esc), routed through the stage's single
			// exit dispatcher — the old list of four named exits missed the spinner, vinyl and 3D kinds.
			if (event.target.closest("[data-hero-panel-back], [data-hero-panel-close]")) {
				this.stage.exitHero?.();
				return;
			}
			const thumb = event.target.closest("[data-hero-panel-thumb]");
			if (thumb) {
				this._setPanelCoverSlot(thumb.dataset.heroPanelThumb);
				return;
			}
		});

		this.root.addEventListener("input", (event) => {
			const el = event.target.closest("[data-setting]");
			if (el) this._handleSettingInput(el);
		});

		// ESC handling.
		//
		// The browser intercepts Esc when the pointer is locked to release the
		// lock — our keydown listener doesn't fire in that case. So we attach
		// the "open settings on Esc" intent to the pointerlockchange event
		// instead: whenever the cursor frees, open settings (unless a hero is
		// already active — that's the user wanting to exit the hero first).
		//
		// The keydown listener still handles Esc when the pointer is already
		// unlocked (settings panel scope, or after a hero has been exited).
		document.addEventListener("pointerlockchange", () => {
			// Refresh the click-to-play overlay either way (lock or unlock).
			this.updateEngagePromptVisibility();
			if (document.pointerLockElement) return;             // just locked
			// ASK-172 §8 (Client 5b) — a surface that takes the KEYS freed the cursor on purpose (Enter → the chat line):
			// not a pause, so the Settings surface must not open over it (releasePointerFor)
			if (this._pointerReleaseFor) { this._pointerReleaseFor = null; return; }
			if (this._isEditMode()) return;                      // Edit frees the cursor on purpose — never pause
			if (this.state.settingsOpen) return;                 // already open
			// Inspect-modal: unlocking while a hero/product is active is the
			// INTENDED state (cursor free over the hero panel) — not an exit
			// signal. Esc-while-unlocked (keydown below) is what puts it back.
			if (this.stage?.activeFixtureHero || this.state.selectedComic?.product) return;
			this.openSettings();
		});

		// Verb key: B (bag/buy) adds the inspected product to the cart without
		// the cursor (loot-prompt pattern). NOT F — F is the comic page-flip
		// (stage KeyF), a collision the human tester caught on day one.
		window.addEventListener("keydown", (event) => {
			if (event.key !== "b" && event.key !== "B") return;
			if (!this.state.selectedComic?.product) return;
			if (isEditableTarget(event.target)) return;   // ASK-180 §3.11 item 4: the one focus rule
			event.preventDefault();
			this.addSelectedComicToCart();
		});

		window.addEventListener("keydown", (event) => {
			if (event.key !== "Escape") return;
			// Edit: Esc is cancel/deselect and NEVER opens the pause surface. (Per-selection
			// deselect lands with the Inspector's selection model; today it simply consumes Esc.)
			if (this._isEditMode()) {
				event.stopPropagation();
				this.stage?.cancelEditInteraction?.();
				return;
			}
			if (this.state.settingsOpen) {
				event.stopPropagation();
				this.closeSettings();
				return;
			}
			// Pointer was already unlocked (rare — Esc usually intercepted by
			// the browser to release lock first). Open settings as fallback.
			if (!document.pointerLockElement) {
				event.stopPropagation();
				if (this.stage?.activeFixtureHero || this.state.selectedComic?.product) {
					// Esc here is "put it back"; the hold's end resumes the walk (`_resumeWalk`).
					this.stage.exitHero?.();
				} else {
					this.openSettings();
				}
			}
		}, { capture: true });
	}

	// ── Settings ───────────────────────────────────────────────────────────────

	loadSettings() {
		let store = null;
		try { store = localStorage; } catch { /* storage blocked */ }
		const { settings, audio } = loadAudioPrefs(store);   // folds the legacy audio keys in once and writes the result back
		return { ...DEFAULT_SETTINGS, ...settings, audio };
	}

	saveSettings() {
		try {
			localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(this.state.settings));
		} catch { /* Storage unavailable */ }
	}

	applySettings() {
		this.state.settings.audio = normaliseAudio(this.state.settings.audio);
		this.stage.setAudioPrefs?.(this.state.settings.audio);
	}

	/** ASK-174 — the ONE writer of the audio model (Settings → Audio, and the hub's mute pill through the stage). */
	setAudioPrefs(next) {
		this.state.settings.audio = normaliseAudio({ ...this.state.settings.audio, ...next });
		this.saveSettings();
		this.applySettings();
		this.renderSettings();
		return this.state.settings.audio;
	}

	// ASK-087 ruled state machine: the pause/settings surface is PLAY-ONLY. Edit owns the cursor —
	// it must stay free so OrbitControls can orbit, and the pause surface must never appear there.
	_isEditMode() { return this.state?.editorMode === "edit"; }

	/** ASK-139 F-139c — the primary pointer is a finger (or the shopper has touched the world). */
	// 🔴 F-139d — this read `stage._touchWalk`, which became a LAZY chunk: at mount it did not exist yet,
	// so the first visibility pass drew "Click to play · W A S D" on a phone and nothing re-ran it (Coord,
	// ASK-139 §19, both devices). Touch-first is a DEVICE fact, so it is read from the device: the primary
	// pointer's media query, or a finger having touched the page (`_sawTouch`, latched in bindEvents).
	_touchFirst() {
		try { if (window.matchMedia?.("(pointer: coarse)").matches) return true; } catch { /* no matchMedia */ }
		return !!this._sawTouch;
	}

	_syncInspectInset() {
		const p = this.refs.heroPanel;
		if (!p || !this.stage?.setInspectInset) return;
		const side = getComputedStyle(p).getPropertyValue("--kx-sheet").trim() !== "bottom";
		// ASK-139 §94.7-1 — on a PHONE (bottom sheet, or the landscape side sheet) the held item is framed inside
		// the HUD safe area: below the compass band and the safe-area top, above/beside the sheet.
		const top = this._phoneLayout() ? this._hudTopBand() : 0;
		this.stage.setInspectInset(side ? { right: p.offsetWidth, top } : { bottom: p.offsetHeight, top });
	}

	/** The phone layouts — the same breakpoints the stylesheet uses for the bottom and the landscape side sheet. */
	_phoneLayout() {
		try { return window.matchMedia("(max-width: 1023.98px), (orientation: landscape) and (max-height: 500px)").matches; } catch { return false; }
	}

	/** The bottom of the HUD's top band in CSS px: the compass (strip + heading) or the safe-area inset, whichever
	 *  is lower, plus an 8 px breath. Measured, never assumed — the compass is laid out whenever the world is. */
	_hudTopBand() {
		let safe = 0;
		try {
			const probe = (this._safeProbe ||= Object.assign(document.body.appendChild(document.createElement("div")), { className: "kapowx-safe-probe" }));
			safe = parseFloat(getComputedStyle(probe).paddingTop) || 0;
		} catch { /* no layout */ }
		const c = this.stage?._compassEl?.getBoundingClientRect?.();
		const compass = c && c.height > 0 ? c.bottom : 0;
		return Math.ceil(Math.max(safe, compass) + 8);
	}

	/** R / Esc / Put back: the shopper is back in the aisle exactly where she stood (the camera never
	 *  moved), with the crosshair live again. Deferred one tick so a hold that hands straight over to
	 *  the next item (browse) does not bounce the lock; every caller is inside a user gesture. */
	_resumeWalk() {
		setTimeout(() => {
			if (this.state.selectedComic?.product || this._isEditMode() || this.state.settingsOpen || this.state.cartOpen) return;
			if (this._touchFirst()) return;   // ASK-139 F-139c — a phone walks with its thumbs, it never locks
			const canvas = this.stage?.renderer?.domElement;
			if (!canvas || document.pointerLockElement === canvas) return;
			const p = canvas.requestPointerLock?.();
			if (p && typeof p.catch === "function") p.catch(() => {});
		}, 0);
	}

	openSettings() {
		this.state.settingsOpen = true;
		this.refs.settingsOverlay.hidden = false;
		this.renderSettings();
		this.updateEngagePromptVisibility();
	}

	closeSettings({ relock = true } = {}) {
		this.state.settingsOpen = false;
		this.refs.settingsOverlay.hidden = true;
		// Re-acquire pointer-lock so the player goes straight back to crosshair
		// navigation. closeSettings is always called from a user gesture (Esc
		// keydown, click on close, or backdrop click) so the request will
		// succeed in modern browsers — Chrome's 1.5s cooldown after a recent
		// exitPointerLock can reject; the promise.catch swallows that. If the
		// request is rejected, the engage prompt will show and the player can
		// click to re-lock.
		// PLAY-ONLY: re-locking in Edit would freeze navigation — pointer-lock gates the FPS rig
		// (by design) AND starves OrbitControls of a cursor, so neither camera system responds.
		const canvas = this.stage?.renderer?.domElement;
		if (relock && !this._touchFirst() && !this._isEditMode() && canvas && document.pointerLockElement !== canvas) {
			const p = canvas.requestPointerLock?.();
			if (p && typeof p.catch === "function") p.catch(() => {});
		}
		this.updateEngagePromptVisibility();
	}

	renderSettings() {
		const s = this.state.settings;
		const panel = this.refs.settingsOverlay.querySelector("[data-runtime-settings-panel]");
		if (!panel) return;

		const a = normaliseAudio(s.audio);
		const volSlider = panel.querySelector("[data-setting='master']");
		if (volSlider) { volSlider.setAttribute("value", a.master); volSlider.value = a.master; volSlider.setAttribute("aria-valuetext", `${a.master} %`); }

		for (const key of ["ambient", "cues"]) {
			const btn = panel.querySelector(`[data-setting='${key}']`);
			if (btn) {
				const on = a[key];
				btn.dataset.on = on;
				btn.setAttribute("aria-pressed", String(on));
				btn.textContent = on ? "ON" : "OFF";
			}
		}
		void this._social().then((m) => m.renderAvatarSettings(this, panel));
	}

	// ASK-167 step 4 + ASK-169 (Client 4w) — the avatar pick and presence live in the lazy `_social.js` (the customer's
	// initial JS carries only these two lines); it loads once the first scene stands, so the panel and the room are
	// ready long before a shopper opens Settings.
	_social() { return (this._socialP ||= import("./_social.js")); }

	_handleSettingToggle(el) {
		const key = el.dataset.setting;
		if (key === "ambient" || key === "cues") this.setAudioPrefs({ [key]: !normaliseAudio(this.state.settings.audio)[key] });
	}

	_handleSettingInput(el) {
		const key = el.dataset.setting;
		if (key === "master") {
			this.state.settings.audio = normaliseAudio({ ...this.state.settings.audio, master: Number(el.value) });
			this.saveSettings();
			this.applySettings();
		}
	}

	// Build the projectId → bootstrap map. Prefer config.shops (the
	// canonical multi-tenant surface); fall back to legacy top-level keys
	// for cached bundles that haven't seen the new shape yet. Demo-store
	// in the legacy path uses state.scene + config.projectAssets which
	// originated from publishedScene / projectAssets top-level keys.
	_assembleProjectBootstraps(config) {
		if (config.shops && typeof config.shops === "object") {
			return { ...config.shops };
		}
		// Legacy fallback — same merchant set as before the shops-map landed.
		const out = {};
		if (this.state.scene) out["demo-store"]     = { scene: this.state.scene, projectAssets: config.projectAssets };
		if (config.hubBootstrap)           out["galleria-hub"]   = config.hubBootstrap;
		if (config.nocturnBootstrap)       out["nocturn"]        = config.nocturnBootstrap;
		if (config.monsterSquadBootstrap)  out["monster-squad"]  = config.monsterSquadBootstrap;
		return out;
	}

	/** ASK-131 D6 — the load-failure surface, imported LAZILY: it fires on a CDN outage and on nothing
	 *  else, and the customer's initial JS is at its ceiling. Async and un-awaited by the caller — the
	 *  console.error below is the part that must not depend on a chunk fetch that may itself fail. */
	async _showBootLoadError(failure) {
		console.error(`[kapowx] boot unreachable — ${failure.message} after ${failure.attempts} attempts (${failure.url})`);
		if (this.refs.empty) this.refs.empty.hidden = true;
		const host = this.refs.empty?.parentElement || this.root;
		try {
			const mod = await import("./_boot-load-error.js");
			mod.showBootLoadError(host, failure, () => window.location.reload());
		} catch (err) {
			console.error("[kapowx] …and the failure surface itself could not load:", err?.message || err);
		}
	}

	bootstrap() {
		const hubBootstrap = this.projectBootstraps["galleria-hub"];
		const hubAvailable = !!hubBootstrap?.scene;
		const shopAvailable = this.state.scene && this.getRuntimeFixtures().length;
		if (!hubAvailable && !shopAvailable) {
			// 🔴 ASK-131 D6 — two ways to have nothing to show, and they are not the same message.
			// "The boot did not arrive" is ours to fix and worth retrying; "the boot arrived and
			// carries no scene" is the merchant's to fix by publishing one. Telling a customer whose
			// CDN blipped to go and publish a scene is a false sentence AND a lost report.
			const failure = this.config?._bootFetchError;
			if (failure) {
				void this._showBootLoadError(failure);
				return;
			}
			this.refs.empty.hidden = false;
			return;
		}
		this.root.querySelector("[data-runtime-load-error]")?.setAttribute("hidden", "");
		this.refs.empty.hidden = true;
		void this._initialBoot();
	}

	async _initialBoot() {
		// Products are fetched lazily per shop on portal entry (see
		// `_fetchProductsForShop` + `navigateToProject`). No eager fetch at
		// boot — multi-shop architecture, only one shop's catalog is needed
		// at a time and the hub itself has no commerce.
		// Hydrate cart + warm capabilities cache in parallel with scene load —
		// no need to block the player's first frame on either. Both fail
		// silently if the gateway is unreachable; checkout button still works
		// (falls back to redirect via the legacy /checkout no-body path).
		// ASK-139 F-139c — a deep link into a shop binds that SHOP's backend on entry; reading the boot
		// default's cart first would show another backend's lines before the shop's own arrive.
		const deepLinking = (() => { try { return new URLSearchParams(window.location.search).has("shop"); } catch { return false; } })();
		if (!deepLinking) {
			this._cartHydrated = true;
			void this.cartService.hydrate();
		}
		// Deep-link: `?shop=<id>` boots STRAIGHT into a shop, skipping the hub.
		// This is how a merchant reaches their own freshly-provisioned shop
		// before the hub has a portal authored to it (Kapow today), and the
		// target of the console's "Open in Studio" door (ASK-103 slice E) and
		// the Studio "View your shop →" affordance alike.
		//
		// ASK-103 slice E — an id the boot has never heard of no longer falls through in SILENCE.
		// It used to land on the hub looking like a normal arrival, which is the user-facing face of
		// the orphan-grant defect (D2): the console offers a shop, the door opens on somewhere else,
		// and nothing says which of the two lied. `_doorEntry` carries it to the toast below.
		this._doorEntry = (() => {
			try {
				const { shop, mode } = parseDoorParams(window.location.search);
				const known = Object.keys(this.projectBootstraps || {}).filter((id) => this.projectBootstraps[id]?.scene);
				return { ...resolveDeepLinkShop({ requestedShop: shop, knownShopIds: known }), mode };
			} catch { return { shopId: null, unknown: null, mode: null }; }
		})();
		const deepLinkShop = this._doorEntry.shopId;
		// ASK-142 R2c — a later compose (a portal hop) re-places an already-built companion behind its loader.
		this.stage.beforeFirstFrame = () => this._bailey?.mountBaileyIfReady(this.stage);   // the module is lazy (below): before it lands there is no built companion to re-place
		// ASK-178 §6 item 6 (D-088 cl.3): the proxy street forms behind a TRANSLUCENT threshold card naming the place; it lifts with the loader
		this.stage.onHubProxy = (phase, card) => { const l = this.refs.loader; if (phase === "show") { this._showTeleportLoader(card); l.setAttribute("data-proxy", ""); } else l.removeAttribute("data-proxy"); };
		this.stage.onCompanion = (b, on) => import("./_companion-sheet.js").then((m) => m.companionSheet(this, b, on)).catch(() => {});   // ASK-162 option C
		// Boot into the hub if available so the player starts in the middle of
		// the street and chooses which shop to enter. Falls back to the comic
		// shop if no hub is configured (e.g. local dev without galleria-hub).
		if (deepLinkShop) {
			await this.navigateToProject(deepLinkShop);
		} else if (this.projectBootstraps["galleria-hub"]?.scene) {
			await this.navigateToProject('galleria-hub');
		} else {
			await this.navigateTo();
		}
		// M7 Chunk 3 (WS5 §E) — start the live customer refresh poller once the first
		// scene is up: a new bake (librarySha change) hot-swaps the scene at an idle
		// boundary, camera + cart preserved (ASK-080, ratified 2026-06-23).
		this._startLiveRefresh();

		// Telemetry-0: page-load event after first scene is composed. Total
		// load = bootStart → here. assetBytes from Resource Timing API
		// (sum of transferSize across all resources fetched so far).
		const loadTimeMs = Date.now() - this.state.bootStartMs;
		const assetBytes = (typeof performance !== "undefined" && typeof performance.getEntriesByType === "function")
			? performance.getEntriesByType("resource").reduce((sum, r) => sum + (r.transferSize || 0), 0)
			: 0;
		this._emitTelemetry("perf:page-load", { loadTimeMs, assetBytes });
		// ASK-139 F-139c (2) — a touch-first device gets the thumbs card (once per device), not the keys.
		if (this._touchFirst()) this._touchHint?.maybeShow();
		else this.showControlsHint();
		// ASK-103 slice E — the door landed nowhere. Say it, name the id, and stay where we are. No
		// grant is inspected here: whether the shop is missing because the grant is stale or because
		// the mint hasn't run, the honest sentence is the same and the runtime can't tell them apart.
		if (this._doorEntry?.unknown) {
			this._toast(`Shop “${this._doorEntry.unknown}” isn’t published yet — showing the hub instead.`, { autohideMs: 8000 });
			console.warn(`[doors] ?shop=${this._doorEntry.unknown} is not in this boot — fell through to the hub`);
		}
		// Shop Design Studio is a role-gated overlay — lazy-loaded only when
		// the signed-in user has `role=merchant` (or admin, for testing).
		// Customers never pay the bundle cost. See documentation/SHOP_TEMPLATE_*
		// and the platform-urls memory.
		void this._maybeLoadStudio();
		// companions: bailey ─────────────────────────────────────────────
		// Local companion dog. Fire-and-forget; load failure logs but
		// doesn't block the boot. The module self-respawns on portal hops
		// by watching stage.currentToken — no further wiring here.
		// ASK-142 R2c — fetched now, built in slices, placed with his shaders already compiled.
		// ASK-159 §2.18es — the module itself is a lazy chunk (~10 KB off every visitor's first bytes): he was already
		// fetched and built in the background, so nothing waited on these bytes that does not now.
		void import("./companions/bailey.js").then((m) => { this._bailey = m; return m.mountBaileyInBackground(this.stage); }).catch((err) => console.warn("[bailey] mount failed:", err));
		// companions: end ────────────────────────────────────────────────
	}

	// ASK-103 slice E — where the Studio's "↩ Console" door points, resolved fresh on every portal
	// hop so it always names the shop the merchant is standing in. Kept HERE rather than in the
	// chrome module: the shop context and the console origin are the app's facts, and the URL grammar
	// belongs to `_cross-app-doors.js`. The chrome only renders what this returns.
	_consoleDoor() {
		const projectId = this.state?.activeProjectId || null;
		const kind = this.projectBootstraps?.[projectId]?.kind || null;
		const shopId = doorShopId({ projectId, kind });
		const href = consoleUrlForShop({ consoleBaseUrl: this.config?.consoleUrl || consoleOriginForPlane(planeWord()), shopId });
		if (!href) return null;
		return {
			href,
			title: shopId ? `Open ${shopId} in the console — shop settings, products, orders` : "Open the console",
		};
	}

	async _maybeLoadStudio() {
		// Single-flight: every caller shares ONE load/mount. Re-entry (second call sites, retries)
		// otherwise races two mounts — the double-panel class fixed in mountStudio; this guard stops
		// the race before it starts. Cleared on failure so a transient import error stays retryable.
		if (this._studioLoadPromise) return this._studioLoadPromise;
		return (this._studioLoadPromise = this._loadStudioInner().catch((e) => { this._studioLoadPromise = null; throw e; }));
	}

	/** The session's authoring role — ONE answer for "may this session author?" (the Studio gate) and
	 *  "does this session read the feed live?" (the first compose). Resolved authority wins in production —
	 *  `config.authority` is produced by [[_authority]] (`/admin/me` when present, claim union otherwise;
	 *  ASK-104 ship 4), with `userRole` kept as the same-value legacy alias. URL param `?role=merchant` or
	 *  `?role=admin` is a dev override — only honoured on local/dev hosts so a stray query string in a
	 *  production share-link can't expose the Studio to a customer. */
	_authoringRole() {
		let role = this.config?.authority?.role || this.config?.userRole;
		if (!role) {
			const params = new URLSearchParams(window.location.search);
			const queryRole = params.get("role");
			const host = window.location.hostname;
			const isDevHost = host === "127.0.0.1" || host === "localhost"
				|| host.startsWith("dev.") || host.endsWith(".web.app");
			if (queryRole && isDevHost) role = queryRole;
		}
		return role === "merchant" || role === "admin" ? role : null;
	}

	/** ASK-159 §2.18is (Client 5e) — the quiet "not your shop" state: an operator (admin) in a shop it does not administer sees
	 *  a small status chip instead of merchant panels (Shopify staff / Roblox Studio: "you don't have permission to edit this
	 *  place" — said once, quietly, never an error). Shown and cleared on every shop entry. */
	_syncNotYourShop() {
		const shopId = this.state?.activeProjectId || null;
		const on = !!this.config?.authority?.role && operatorForeignShop(this.config.authority, { shopId, isShop: isMerchantShopScene(this) });
		let el = this.root?.querySelector?.("[data-studio-not-your-shop]") || null;
		if (!on) { el?.remove(); return; }
		if (!el) {
			el = document.createElement("div");
			el.setAttribute("data-studio-not-your-shop", "");
			el.setAttribute("role", "status");
			el.setAttribute("data-hud", "");
			el.className = "kapowx-not-your-shop";
			// beside the HUD pills, inside the store root (a node on <body> draws under the canvas — §presence hudHost)
			const pill = this.refs?.presenceCount || this.root?.querySelector?.("[data-runtime-presence-count]");
			(pill?.parentElement || this.root || document.body).appendChild(el);
		}
		el.dataset.shop = shopId || "";
		el.textContent = "Operator view · not your shop";
	}

	async _loadStudioInner() {
		await this.config?.authorityReady;   // D-088 Slice 0: the authority read runs beside the app's construction — read it here
		const role = this._authoringRole();
		if (!role) return;
		// ASK-158 step 1 (S2) — the street is the platform's: the shop Studio never mounts on the hub. `?mode=edit` there
		// resolves by role (lazy `studio/_hub-edit-door.js`): operator → the Street panel, merchant → the plain state +
		// a link to their shop. The Studio mounts on the first walk into a shop (`navigateToProject`).
		if (this.state?.activeProjectId && !isMerchantShopScene(this)) {
			this._studioDeferredOnHub = true;
			if (this._doorEntry?.mode && this._doorEntry.mode !== "play") {
				import("./studio/_hub-edit-door.js").then((m) => m.mountHubEditDoor({ app: this, role })).catch((e) => console.warn("[doors] hub edit door:", e?.message || e));
			}
			return;
		}
		// ASK-142 F-142d (a) — a merchant whose authority lists their shops gets no Studio on a shop that
		// is not on the list (Shaun on Vanessa's shop: 12 refused reads → "sign-in expired"). Only an
		// authority-derived role is scoped; the dev `?role=` override stays what it was.
		const shopId = this.state?.activeProjectId || null;
		if (this.config?.authority?.role && !mayAuthorShop(this.config.authority, { shopId, isShop: isMerchantShopScene(this) })) {
			// ASK-159 §2.18is (Client 5e): the grant is read BEFORE any merchant panel mounts — no panel, no shop-scoped GET (an
			// operator on a shop it does not administer drew four 403s into the console). An operator gets the operator view +
			// the quiet "not your shop" state; walking into a shop it DOES hold later mounts the Studio there.
			this._studioDeferredForeign = true;
			this._syncNotYourShop();
			console.log(`[studio] not mounted — ${role} has no grant on ${shopId}; ${role === "admin" ? "operator view (not your shop)" : "customer view"}`);
			return;
		}
		try {
			const mod = await import("./studio/index.js");
			await mod.mountStudio({ app: this, role });
		} catch (err) {
			console.error("[studio] lazy-load failed:", err);
		}
		// …and where the authority had no list, the hydrate's first refused read already demoted us.
		if (this._authoringDeniedShop && this._authoringDeniedShop === this.state?.activeProjectId) return;
		// ASK-087 slice-a — mount the Edit/Play mode-switch skeleton alongside the overlay. Lazy +
		// role-gated (this whole block only runs for merchant/admin), so it stays out of the
		// customer initial bundle. Separate module so the frozen overlay (studio/index.js) is
		// untouched; slice-b/c wire the Inspector + cameras to its `galleria:editor-mode` event.
		try {
			const modeMod = await import("./studio/editor-mode.js");
			// ASK-096: the Shell-compose mode is offered ONLY when the shop is composition-backed — a
			// persisted module composition on the scene (GCG persistence, ASK-096 Q2, when it lands) OR
			// the dev override `?compose=1` for pre-persistence testing. A fixed-shell shop (demo-store)
			// never grows the affordance, so it can't enter compose — golden-safe.
			// ASK-100 ④ K3 (S5c) — composition-backed is read from the PERSISTED truth: the GCG
			// shell-composition doc (the boot scene doesn't carry it). Scene fallbacks + the dev
			// `?compose=1` override remain; the GET is merchant-authed and fail-soft.
			let shellCompositionDoc = null;
			// ASK-129 §E2E102-rerun item 3 — the hub has no shell-composition doc and its
			// /shell-composition 404s. Same one predicate as every other merchant-API surface.
			try {
				const txMod = isMerchantShopScene(this) ? await import("./studio-v1/shell/_composition-transport.js") : null;
				const res = txMod ? await txMod.fetchShellComposition(this.config, this.state?.activeProjectId) : { status: 0, doc: null };
				if (res.status === 200 && Array.isArray(res.doc?.composition) && res.doc.composition.length) {
					shellCompositionDoc = res.doc;
				}
			} catch (err) {
				console.warn("[studio] shell-composition fetch failed:", err?.message || err);
			}
			const compositionBacked = !!shellCompositionDoc
				|| !!(this.state?.scene?.shellComposition?.composition)
				|| !!(this.state?.scene?.shopGeometryBinding?.composition)
				|| new URLSearchParams(window.location.search).get("compose") === "1";
			this._editorMode = modeMod.mountEditorMode({ app: this, compositionBacked, resolveConsoleDoor: () => this._consoleDoor() });
			// ASK-100 ④ K2 — the compose controller (palette + ghost + seam-snap + commit) rides the
			// same composition-backed gate; lazy so shell machinery never loads on a fixed-shell shop.
			if (compositionBacked && this.stage) {
				try {
					const composeMod = await import("./studio-v1/shell/compose-controller.js");
					this._composeController = composeMod.mountComposeController({ app: this, stage: this.stage, initialDoc: shellCompositionDoc });
				} catch (err) {
					console.error("[studio] compose-controller mount failed:", err);
				}
			}
			// ASK-087 slice-c — drive the stage camera off the mode: Play = immersive FPS,
			// Edit = free orbit. Wired here (the coordinator) so the stage method stays a pure
			// toggle; the Inspector already keys its own visibility off the same event.
			this.stage?.setCameraMode?.(this.state?.editorMode || "play");
			if (!this._editorModeCameraBound) {
				this._editorModeCameraBound = true;
				window.addEventListener("galleria:editor-mode", (ev) => {
					const mode = ev?.detail?.mode;
					// Edit entry: auto-dismiss the Play-only pause surface and free the cursor.
					// closeSettings() no longer re-locks in Edit, so the cursor stays free for orbit.
					if (mode === "edit" && this.state.settingsOpen) this.closeSettings();
					this.stage?.setCameraMode?.(mode);
				});
			}
			// ASK-103 slice E — the door INTO the Studio. `?mode=edit` lands in the authoring surface
			// instead of the customer view. Applied LAST, so every consumer of the mode event (camera,
			// Inspector visibility, compose controller) is already subscribed and the entry is
			// indistinguishable from a click on the toggle. It runs inside the role-gated block, so the
			// link can only ever reach a mode this identity could have reached by clicking — the URL
			// points, it never grants (S1/S4).
			const entryMode = resolveEntryMode({ requestedMode: this._doorEntry?.mode, role, compositionBacked });
			if (entryMode !== "play") {
				this._editorMode?.setMode(entryMode);
				console.log(`[doors] entered via the console door — mode=${entryMode}`);
			}
		} catch (err) {
			console.error("[studio] editor-mode mount failed:", err);
		}
	}

	// Swap to a new scene package. Pass overrides for any of scene/products/
	// projectAssets to update state before loading; omit to reuse the existing
	// state.scene (used by the initial boot).
	async navigateTo({ scene, products, projectAssets } = {}) {
		this.state.isLoading = true;
		this._showTeleportLoader();
		// 2c — staged 3D progress: real byte-progress on the shell GLB (dominant asset)
		// drives a labelled bar inside the loader, replacing the opaque spinner (ASK-080).
		const sceneProg = this._sceneProgress();

		if (scene)         this.state.scene = scene;
		if (products)      this.state.products = products;
		if (projectAssets) {
			// Merge so hopping between projects preserves keys from earlier loads
			// (e.g. hopping to hub then back to demo-store still has `comic`, `longbox`, etc.)
			this.config.projectAssets = { ...(this.config.projectAssets || {}), ...projectAssets };
		}

		// Telemetry-0: measure compose time across the setFullSceneContext call.
		const composeStartMs = Date.now();
		let report;
		try {
			report = await this.stage.setFullSceneContext({
				scene:             this.state.scene,
				products:          this.state.products,
				projectAssets:     this.config.projectAssets,
				// ASK-099 item 1 — the active shopId + the boot MINT sha (from `generatedFrom`'s @sha),
				// so per-shop minted assets (recipe env HDRIs at ${CDN}/${shopId}/${sha}/env/…) resolve
				// even when the shop's shell carries no assetKey (test-shop uses the shared template-
				// registry shell ref → no per-shop sha in its own data).
				shopId:            this.state.activeProjectId,
				librarySha:        (this.config?.generatedFrom || "").split("@")[1] || null,
				// ASK-124 B3 — the pin of the TEMPLATES tree the lighting recipes came from.
				// A recipe's env HDRI is family data, not shop data, so it resolves against
				// `templates/${templatesSha}/` — one copy per pin instead of one per stamped
				// shop per pin (measured: 907 HDR objects / 3.909 GB / 129x duplication).
				templatesSha:      this.config?.templatesSha || (this.config?.generatedFrom || "").split("@")[1] || null,
				templatePresets:   this.config.templatePresets || null,
				templateCatalogue: this.config.templateCatalogue || null,   // ASK-080 Track-1 diagnose: swatch-source check
				materialLibrary:   this.config.materialLibrary || null,             // DECISION-053 Slice-2: shared map-set library
				templateSurfaceMaterials: this.config.templateSurfaceMaterials || null,   // DECISION-053 Slice-2: slot→material bindings
				fixtureCatalogue:  this.config.fixtureCatalogue || null,
				// DECISION-026 Phase 2 — per-kapowxProductType surfaceSlots[]
				// registry (live via boot.json.productTypes per ASK-047 ship).
				// Falls back to null when boot.json hasn't yet baked the
				// ASK-047 emit (very old runtime builds); stage's legacy
				// single-material path remains active in that case.
				productTypes:      this.config.productTypes || null,
				// P2-E2 ruling 2 — the product-mesh source registry (concrete-type key or a
				// line's renderAssetKey resolves through it; see resolveProductRenderSource).
				productCatalogue:  this.config.productCatalogue || null,
				onProgress:        (ev) => sceneProg.onAsset(ev),
				// ASK-050 — canonical merchant identity for signage. Same source
				// as _resolveSellerOfRecord (commerce.storeName) so the sign and
				// the seller-of-record disclosure can never disagree on who the
				// merchant is. knobs.shopName (Studio knob) still overrides.
				commerce:          this.config?.shops?.[this.state.activeProjectId]?.commerce || null,
				// ASK-129 §E2E102 F5 — the boot's own title for this shop, the LAST resort before the
				// bare word "Shop". Measured on the ladder shop: every other source in the chain
				// (shopName knob, commerce.storeName, semantic.name, metadata.title) is empty, so the
				// sign read "Shop". This is the same field §vacant-sign resolved the hub door's sign
				// from — one shop name, rendered on both sides of the door.
				shopTitle:         this.config?.shops?.[this.state.activeProjectId]?.title || null,
			});
			// Re-apply grey-mode (and audio settings) against the freshly-loaded scene's
			// materials so the toggle state survives portal hops. Wrapped separately so
			// a settings error never blocks the loader from hiding.
			try {
				this.applySettings();
			} catch (settingsErr) {
				console.error('[kapowx] applySettings error:', settingsErr);
			}
		} catch (err) {
			console.error('[kapowx] Scene load error:', err);
		} finally {
			this.state.isLoading = false;
			if (report) this._refreshStageStatus();
			sceneProg.done();
			// D-088 cl.1 Slice 0 (ASK-178 §3 item 2): the street's overlay hides ON its first composed frame (setFullSceneContext
			// already drew it behind the loader) — no 0.9 s fade over a street that is ready. A shop keeps its fade.
			const onHub = this.stage?.sceneKind === "hub" && !!this.stage?._hubLife;
			// D-K2-4 (coord ASK-180 §3m): a street that could not be built keeps the card up and SAYS so (lazy: it fires on a failure only)
			const hubFailed = this.stage?._hubBuildFailure;
			if (hubFailed) void import("./_hub-build-failure.js").then((m) => m.showHubBuildFailure(this, hubFailed)).catch((e) => console.error("[kapowx] the failure card could not load:", e?.message || e));
			else { this.refs.loader?.removeAttribute("data-failed"); this.root?.removeAttribute("data-hub-failed"); this._hideTeleportLoader({ immediate: onHub }); }
			if (onHub && typeof performance !== "undefined") try { performance.mark("gg:hub-first-frame"); } catch { /* User Timing absent */ }
			void this._social().then((m) => m.afterCompose(this)).catch((e) => console.warn("[social] unavailable:", e?.message || e));   // ASK-169 — this shop's room · ASK-167 — the profile's pick
			// Notify subscribers (e.g. the Shop Design Studio overlay) that
			// the active scene has swapped, so they can re-read knobs.
			if (typeof this._onSceneChange === "function") {
				try { this._onSceneChange(); } catch (cbErr) { console.error('[kapowx] onSceneChange error:', cbErr); }
			}
			// Telemetry-0: emit perf:scene-composed for every scene swap.
			// Fixture count read from the runtime's published metadata where
			// available, falling back to the in-memory scene's semantic
			// fixtures list. shopId is the active project (e.g. demo-store,
			// nocturn, monster-squad, or galleria-hub for the hub itself).
			const fixtureCount = this.stage?._runtimeMetadata?.length
				?? this.state.scene?.semantic?.fixtures?.length
				?? 0;
			this._emitTelemetry("perf:scene-composed", {
				shopId:        this.state.activeProjectId || null,
				fixtureCount,
				composeTimeMs: Date.now() - composeStartMs,
			});

			// Telemetry-0 M5 C11: `visit` business event — fires once per
			// shop-scene compose. Distinct from perf:scene-composed (engineering)
			// — this is the merchant-facing visit metric per DECISION-031 §1
			// (cost-to-serve banding for DECISION-032 subscription tiers per v2).
			// Counted server-side; bot-filter + dedup happens at GCG aggregation
			// per DECISION-032 §3 + §11 (v2 co-defines billable-visit unit).
			this._emitTelemetry("visit", {
				shopId:           this.state.activeProjectId || null,
				referrer:         typeof document !== "undefined" ? (document.referrer || null) : null,
				userAgent:        typeof navigator !== "undefined" ? (navigator.userAgent || null) : null,
				viewport: typeof window !== "undefined" ? {
					width:  window.innerWidth,
					height: window.innerHeight,
				} : null,
			});
		}
	}

	// Convenience: navigate to the hub project bootstrap supplied by PHP at boot.
	async navigateToHub() {
		await this.navigateToProject('galleria-hub');
	}

	// Generic project navigation. Looks up the project's bootstrap, optionally
	// passes a spawnRef so the destination scene picks the matching arrival
	// position from its scene.spawns map.
	async navigateToProject(projectId, spawnRef, { acknowledged = false, card } = {}) {
		const boot = this.projectBootstraps[projectId];
		if (!boot?.scene) {
			console.warn(`[kapowx] No bootstrap for project '${projectId}'.`);
			return;
		}
		const fromProjectId = this.state.activeProjectId;   // ASK-146 §17.13-1: the shop being left picks the hub arrival
		this.state.activeProjectId = projectId;
		// ASK-174 — crossing a shop door: the door at the door (the walker is AT it) + the per-listener snapshot (the street
		// ducks −12 dB through an 800 Hz lowpass over 600 ms going in, the same curve reversed coming out; kept alive)
		const fromKind = this.stage.sceneKind, toKind = boot.scene?.sceneKind === "hub" ? "hub" : "shop";
		if (!acknowledged && fromProjectId && fromKind && fromKind !== toKind) {   // a door path already acknowledged it (_hub-entrance.js)
			const c = this.stage.camera.position;
			this.stage.cue("shop-threshold", { role: "self", pos: { x: c.x, y: c.y - 0.6, z: c.z } });
			this.stage._sound?.threshold(toKind === "shop" ? "in" : "out");
		}
		// ASK-139 F-139c — the 4-way standard: the loader is up from the FIRST moment of entry, not
		// from after the feed fetch (measured on a phone: the feed round-trip was a black stage).
		this._showTeleportLoader(card);

		// Fetch this shop's product catalog lazily on portal entry. Hub
		// projects skip the fetch (no commerce). Cache hit = instant; cold
		// fetch adds a network round-trip to the portal-loader window. Fetch
		// failures resolve to an empty catalog — stock-honest fixture
		// builders render empty geometry gracefully.
		const isShop = boot.kind === "shop";
		// ASK-142 F-142b — an AUTHORING session reads the feed live from its very first compose (ASK-097
		// fix-1's rule, which only the later refetch obeyed). Measured on the plane: a merchant who
		// published records and refreshed got the browser's cached pre-publish feed (max-age 300), the
		// crate composed EMPTY, and the records only arrived through a reseat. Customers stay cached (D-058).
		if (isShop) await this.config?.authorityReady;   // D-088 Slice 0: a shop's feed reads live by the session's authority (the hub reads none)
		const fetchedProducts = isShop ? await this._fetchProductsForShop(projectId, { live: !!this._authoringRole() }) : [];
		if (isShop) void this._bindShopCommerce(projectId);

		// Merge persisted merchant knob overrides into the scene template
		// before composition runs. Single source of truth per shop — both
		// customers (no Studio) and merchants (Studio open) see the same
		// state. Override map is populated by PHP at page load + kept
		// in-memory by Studio after each save.
		const overrides = this.config?.templateKnobOverrides?.[projectId];
		let scene = boot.scene;
		if (scene?.template) {
			// ALWAYS clone the authored knobs — deep, so nested maps (shop / boxOrientations) are
			// copied too — before Studio can touch them. A clean load (no overrides) previously
			// shared boot.scene.template.knobs BY REFERENCE, so live edits mutated the cached
			// bootstrap in place; Reset-to-defaults then rebuilt from the polluted baseline
			// (ASK-086 Am.6). The immutable authored baseline must stay immutable — clone it here,
			// unconditionally, and layer any persisted overrides on top of the copy.
			const baseKnobs = typeof structuredClone === "function"
				? structuredClone(scene.template.knobs || {})
				: JSON.parse(JSON.stringify(scene.template.knobs || {}));
			scene = {
				...scene,
				template: {
					...scene.template,
					knobs: { ...baseKnobs, ...(overrides || {}) },
				},
			};
		}
		// _activeSpawnRef is consumed by the stage's per-scene spawn resolver.
		if (spawnRef) scene = { ...scene, _activeSpawnRef: spawnRef };
		// Hub (criterion 15): overlay TAKEN merchant storefront door-slots so the
		// hub render wires a portal + waypoint to each occupied slot's shop. The
		// registry holds occupancy (not the boot), so fetch it on hub entry.
		if (projectId === "galleria-hub") {
			// 🔴 ASK-129 F-102i — A DOOR AND ITS SIGN ARE TWO QUESTIONS, and this used to answer both
			// with one filter. A registry shopId that doesn't resolve in the boot (N2 holds GCG id
			// `kapowx-coza`, boot key `demo-store`; GCG flag 2026-06-17) must not get a PORTAL — it
			// would lead nowhere, and the storefront's static boot portal already works. But the SIGN
			// above that door needs only a NAME, and dropping the whole row denied it one. Measured
			// 2026-09-19: N2 is the only taken slot on dev and it was being dropped entirely, which is
			// why `probe-hub-signs` was red before this pass on an untouched tree.
			//
			// So every taken slot now reaches the scene, carrying two derived fields: `navigable`
			// (the portal builder's question) and `storefront` (the sign's). The filter became a flag.
			//
			// 🔴 AND THE REGISTRY NOW STATES THE MAPPING — `runtimeShopId` (GCG, 2026-09-20, ASK-129
			// ruling 1). The paragraph above ends "the client cannot invent the mapping", and it no
			// longer has to: the wire carries the BOOT KEY beside the registry id. Measured on the
			// deployed gateway the day it shipped:
			//
			//     N2 · shopId "kapowx-coza" → runtimeShopId "demo-store"
			//     a FREE slot → the field is ABSENT, not null-for-unknown
			//
			// So N2 — the one taken door on dev, nameless and portal-less since 2026-06-17 — resolves.
			// `?? s.shopId` is the documented fallback and not a guess: GCG's rule is that a NULL alias
			// means "keyed verbatim", so a row without one is a row whose shopId IS the boot key.
			const bootKeyOf = (s) => s?.runtimeShopId ?? s?.shopId;
			const registry = await this._fetchHubRegistry();
			scene = { ...scene, doorSlotsFree: registry.free, doorSlotsFreeRows: registry.freeRows, hubThemePick: registry.theme };
			const doorSlotsTaken = registry.taken
				.filter((s) => s && s.shopId)
				.map((s) => ({
					...s,
					bootKey: bootKeyOf(s),
					navigable: !!this.projectBootstraps[bootKeyOf(s)],
					storefront: this._storefrontIdentity(s),
				}));
			if (doorSlotsTaken.length) scene = { ...scene, doorSlotsTaken };
			// 🔴 ASK-146 §17.13-1 — leaving a shop lands at THAT shop's door, not N2's: every exit used to carry the
			// boot's `from-portal-1` (N2). The registry says which slot the shop holds; the arrival is derived from it.
			// Lazy (ASK-157 §4.8 d, a bundle reduction): read only on a shop → hub exit, on a path already awaiting the registry.
			const own = fromProjectId && fromProjectId !== projectId ? (await import("./_hub-door-arrival.js")).exitArrivalFor(fromProjectId, doorSlotsTaken, scene.spawns) : null;
			if (own) {
				scene = { ...scene, spawns: { ...(scene.spawns || {}), [own.ref]: own.spawn }, _activeSpawnRef: own.ref };
				console.log(`[hub] exit from ${fromProjectId} → its own door ${own.slotId} (${own.ref}; was ${spawnRef || "default"})`);
			}
		}
		await this.navigateTo({ scene, products: fetchedProducts, projectAssets: boot.projectAssets });
		// Entering a project is the moment its canonical merchant state becomes relevant. Studio
		// hydrates GCG (/instances, /anchors, /knobs) at MOUNT — which happens in the hub, where
		// _activeIsMerchantShop() is false and every hydrate skips. Walking into your own shop is the
		// normal path AND the path after every reload, so without this a merchant's persisted moves
		// were never fetched: the shop composed from the baked boot every time and the move vanished.
		// Announced rather than called directly — the runtime doesn't depend on Studio existing.
		window.dispatchEvent(new CustomEvent("galleria:project-entered", { detail: { projectId, kind: boot.kind || null } }));
		await this.config?.authorityReady;   // the Studio's deferral + the operator chip read authority (after the scene is up)
		if (this._studioDeferredOnHub && boot.kind !== "hub") { this._studioDeferredOnHub = false; this._studioLoadPromise = null; void this._maybeLoadStudio(); }   // ASK-158 step 1
		else if (this._studioDeferredForeign && boot.kind !== "hub" && mayAuthorShop(this.config?.authority, { shopId: projectId, isShop: isMerchantShopScene(this) })) { this._studioDeferredForeign = false; this._studioLoadPromise = null; void this._maybeLoadStudio(); }   // §2.18is: a shop it holds
		this._syncNotYourShop();
	}

	/**
	 * 🔴 ASK-129 F-102i — ONE STOREFRONT IDENTITY (Coord ruling, 2026-09-19).
	 *
	 * Vanessa: *"hub signage did not change with changes in the studio."* She named her shop in the
	 * Studio, the sign inside the shell changed, and the door on the street kept the name the hub GLB
	 * was published with — because the hub sign read the TENANT row (`title`) while the Studio writes
	 * the STOREFRONT knobs. Two names for one shop, and the merchant had only ever been offered one
	 * place to set it.
	 *
	 * The ruling: the Studio's Sign knobs (`shopName` · `signMode` · `signLogoUrl`) ARE the storefront
	 * — inside the shell AND on the hub door. The tenant `displayName`/`title` stays the console and
	 * billing label. AAA precedent: Shopify's store name vs the tenant account, and Unreal's per-level
	 * display name vs the project name — the merchant designs the front once.
	 *
	 * Resolved HERE rather than in the sign drawer because this is the layer that holds the boot, and
	 * because the drawer was reaching for `stage.projectBootstraps`, which the stage has never had —
	 * a dead branch that silently made every sign fall through to the raw shop id.
	 *
	 * Precedence is `foldShopKnobs`' own, not a second copy of it: `.shop` (what the merchant last
	 * saved) outranks the authored top level (what the scene was published with).
	 */
	_storefrontIdentity(slot) {
		// The BOOT KEY, not the registry id — `runtimeShopId` where GCG sends one, the shopId verbatim
		// where it does not (a null alias means "keyed verbatim", never "unknown"). Both lookups below
		// are keyed on it, because a shop's knobs and its title live under the same key.
		const key = slot?.runtimeShopId ?? slot?.shopId ?? null;
		const shop = key ? this.projectBootstraps?.[key] || null : null;
		const authored = shop?.scene?.template?.knobs || null;
		// Layer this session's SAVED overrides over the baked boot, exactly as `navigateToProject`
		// does before composing a shop. Without it a merchant who renames their shop and walks
		// straight out to the street reads the previous bake on their own door for as long as the
		// next rebake takes — the chrome would be following the round trip instead of the mutation.
		const overrides = key ? this.config?.templateKnobOverrides?.[key] : null;
		const merged = authored ? { ...authored, ...(overrides || {}) } : (overrides || null);
		const knobs = merged ? foldShopKnobs(merged, merged.shop) : null;
		const knobName = typeof knobs?.shopName === "string" && knobs.shopName.trim() ? knobs.shopName.trim() : null;
		// Absent is not empty (ways-of-working §6): a shop that has never set a name falls through to
		// the registry's own label and then to the tenant title, and the leg reports WHICH one spoke.
		// ASK-178 §6 item 4 (Client 5k ruling ii): the registry rung reads `occupantName` — the field the wire carries (GCG
		// `shops.display_name`, the console rename) — never the `shopName` it never sent. Chain: the shop's sign name (Studio
		// knob, this session's save layered over the bake) → the registry's name → the boot title. The door, its nameplate and
		// the shop's own sign read ONE name, and a rename (Studio or console) reaches all three.
		const regName = typeof slot?.occupantName === "string" && slot.occupantName.trim() ? slot.occupantName.trim() : null;
		const name = knobName
			|| regName
			|| (typeof shop?.title === "string" && shop.title.trim() ? shop.title.trim() : null)
			|| null;
		const mode = knobs?.signMode?.preset ?? knobs?.signMode ?? null;
		const logoUrl = typeof knobs?.signLogoUrl === "string" && knobs.signLogoUrl ? knobs.signLogoUrl : null;
		return {
			name,
			// The mode only means "logo" when there IS a logo — a shop left in logo mode after its
			// image was cleared must still get its name on the door, not a blank board.
			signMode: mode === "uploaded-logo" && logoUrl ? "uploaded-logo" : "typographic-shopname",
			logoUrl,
			source: knobName ? "studio-knobs" : (regName ? "registry" : (shop?.title ? "boot-title" : "none")),
		};
	}

	// Taken hub door-slots (merchant storefronts) from GCG's registry. Each
	// carries { slotId, shopId, position, signAnchor, facadeStyle, … }; the hub
	// render turns them into portals + signs. Failure → [] (hub still renders
	// its static portals).
	// ASK-177: ONE read of the whole registry (no status filter) answers three questions — the TAKEN rows (portals +
	// signs, as before), the FREE slot ids (S3: the shutter + TO LET, `null` when the read failed so no shutter drops on
	// a booked door) and GCG's ACTIVE hub theme (S1: `hub.scene.theme`, read where the public hub read carries it).
	async _fetchHubRegistry() {
		const gateway = this.config.gatewayConfig;
		if (!gateway?.baseUrl) return { taken: [], free: null, theme: null };
		try {
			const url = `${gateway.baseUrl.replace(/\/$/, "")}/api/${gateway.apiNamespace || "kapowx/v1"}/hub/door-slots`;
			// ASK-178 §3 item 2 (D-088 Slice 0): the FIRST hub entry takes the read index.html started at parse (same URL);
			// an early read that failed is not used (this entry reads as before)
			const early = await earlyJson("doorSlots", url, { take: true });
			let json;
			if (early?.ok) json = early.json;
			else {
				const resp = await fetch(url);
				if (!resp.ok) return { taken: [], free: null, theme: null };
				json = await resp.json();
			}
			const slots = Array.isArray(json.slots) ? json.slots : [];
			const hub = json.hub && typeof json.hub === "object" ? json.hub : null;
			const theme = hub?.scene?.theme ?? json.scene?.theme ?? null;
			const freeRows = slots.filter((s) => s?.status === "free" && s.slotId);   // D-086 cl.5: the TO LET prompt's `enquiryUrl`
			return {
				taken: slots.filter((s) => s?.status === "taken"),
				free: Array.isArray(json.slots) ? freeRows.map((s) => s.slotId) : null, freeRows,
				theme: theme && typeof theme === "object" && typeof theme.id === "string" ? theme : null,
			};
		} catch (err) {
			console.warn("[hub] door-slots fetch failed:", err?.message || err);
			return { taken: [], free: null, theme: null };
		}
	}

	// ── Live customer refresh (M7 Chunk 3 / WS5 §E, ASK-080) ──
	// Poll the published boot's librarySha; on a new bake, hot-swap the scene at an
	// idle boundary (camera + cart preserved), else surface a gentle nudge if the
	// customer never idles. Decision logic is the unit-tested evaluateRefresh().
	_startLiveRefresh() {
		if (this._liveRefresh) return;
		const bootUrl = this.config?._bootUrl;
		if (!bootUrl || typeof fetch !== "function") return;   // bundled boot / no source → skip
		this._liveRefresh = {
			bootUrl,
			loadedSha: this._libraryShaFor(this.config, this.state.activeProjectId),
			pendingSinceMs: null, lastInputMs: Date.now(), busy: false, nudged: false, timer: null,
		};
		const bump = () => { if (this._liveRefresh) this._liveRefresh.lastInputMs = Date.now(); };
		window.addEventListener("pointerdown", bump, { passive: true });
		window.addEventListener("keydown", bump);
		window.addEventListener("wheel", bump, { passive: true });
		document.addEventListener("visibilitychange", () => { if (!document.hidden) void this._liveRefreshTick(); });
		this._liveRefresh.timer = setInterval(() => void this._liveRefreshTick(), 25000);
	}

	_libraryShaFor(boot, shopId) {
		if (!boot) return null;
		return (shopId && boot.shops?.[shopId]?.librarySha)
			|| boot._coverage?.librarySha || boot.librarySha || boot.generatedAt || null;
	}

	// Open product / hero / checkout / cart / (recent input handled via lull) = busy.
	// An EDIT session is always busy (ASK-159 §2.2d item 2): the Studio already draws the merchant's own writes, and a
	// hot-swap rebuilt every mesh under their selection, drag and Inspector — measured on dev 67fcc35 by the real poll
	// with a newer bake pending at probe-editor-chrome C2: the swap ran (interacting false) and 8 legs read the
	// selection/outline null, 4c's flake signature. A newer bake in Edit gets the "Shop updated · Refresh" nudge instead.
	_isInteracting() {
		if (this._isEditMode()) return true;
		return !!(this.stage?.activeFixtureHero || this.state?.selectedComic?.product || this.state?.checkoutBusy || this.state?.cartOpen);
	}

	async _liveRefreshTick() {
		const lr = this._liveRefresh;
		if (!lr || lr.busy) return;
		lr.busy = true;
		try {
			const resp = await fetch(lr.bootUrl, { cache: "no-store" });
			if (!resp.ok) return;
			const boot = await resp.json();
			// 🔴 ASK-131 §2b — REFUSE A BOOT THIS BUNDLE CANNOT READ, and keep the last good one.
			// This poller merged whatever arrived by blind spread every 25 seconds. A truncated or
			// breaking-renamed publish therefore had a 25-second path into a merchant's LIVE session,
			// and the drift detector beside it could only say "the boot changed" — never "this is a
			// shape I can read". Refusing keeps the session on the config it already has, which is by
			// definition one that worked.
			const schema = readBootSchema(boot);
			if (!schema.ok) {
				// Say it ONCE per distinct reason: a poller that logs every 25s is a log nobody reads.
				if (lr.refusedReason !== schema.reason) {
					lr.refusedReason = schema.reason;
					console.warn(`[live-refresh] ${describeBootSchema(schema)} — keeping the loaded boot; the shop will not hot-swap until a readable one publishes.`);
				}
				return;
			}
			lr.refusedReason = null;
			const latestSha = this._libraryShaFor(boot, this.state.activeProjectId);
			if (latestSha && latestSha !== lr.loadedSha && !lr.pendingSinceMs) lr.pendingSinceMs = Date.now();
			// §2.18jr (WoW §14.5 publish + rebake wait): a NEW bake for the active shop is the cook's end — said once per sha,
			// so a Studio wait (the compose Save's progress bar) ends on the runtime's own measurement, never on a timer
			if (latestSha && latestSha !== lr.loadedSha && lr.landedSha !== latestSha) {
				lr.landedSha = latestSha;
				try { window.dispatchEvent(new CustomEvent("galleria:bake-landed", { detail: { shopId: this.state.activeProjectId, sha: latestSha } })); } catch { /* no window */ }
			}
			const verdict = evaluateRefresh({
				loadedSha: lr.loadedSha, latestSha, interacting: this._isInteracting(),
				nowMs: Date.now(), lastInputMs: lr.lastInputMs, pendingSinceMs: lr.pendingSinceMs,
			});
			if (verdict.state === "swap") await this._performLiveRefresh(boot);
			else if (verdict.state === "nudge" && !lr.nudged) { lr.nudged = true; this._showRefreshNudge(boot); }
		} catch (err) {
			console.info("[live-refresh] poll skipped:", err?.message || err);
		} finally { lr.busy = false; }
	}

	// State-preserving hot-swap: keep camera + orbit target (cart already persists in
	// state). navigateToProject respawns the camera, so we restore it after.
	async _performLiveRefresh(boot) {
		const lr = this._liveRefresh;
		const active = this.state.activeProjectId;
		if (!lr || !boot || !active) return;
		// Re-checked at the swap itself, not only at the poll: `_showRefreshNudge` hands a boot to a
		// BUTTON, and the merchant can press it minutes later. The check belongs where the blind
		// spread is, or it guards the path nobody took.
		const schema = readBootSchema(boot);
		if (!schema.ok) {
			console.warn(`[live-refresh] refusing to swap — ${describeBootSchema(schema)}`);
			this._toast("Update skipped — this shop couldn't read the new data", { autohideMs: 4000 });
			return;
		}
		const cam = this.stage?.camera, ctrl = this.stage?.controls;
		const pos = cam ? { x: cam.position.x, y: cam.position.y, z: cam.position.z } : null;
		const tgt = ctrl?.target ? { x: ctrl.target.x, y: ctrl.target.y, z: ctrl.target.z } : null;
		this.config = { ...this.config, ...boot, _bootUrl: lr.bootUrl };
		this.projectBootstraps = this._assembleProjectBootstraps(this.config);
		await this.navigateToProject(active);
		if (cam && pos) cam.position.set(pos.x, pos.y, pos.z);
		if (ctrl && tgt) { ctrl.target.set(tgt.x, tgt.y, tgt.z); ctrl.update?.(); }
		lr.loadedSha = this._libraryShaFor(boot, active);
		lr.pendingSinceMs = null; lr.nudged = false;
		this._toast("Shop updated", { autohideMs: 2500 });
		loadingAnnounce("Shop updated");
	}

	_showRefreshNudge(boot) {
		this._toast("Shop updated", { actionLabel: "Refresh", onAction: () => void this._performLiveRefresh(boot) });
	}

	// Self-contained toast (2c non-intrusive notice); never blocks. Used by
	// live-refresh + future light surfaces.
	//
	// ASK-135: the five inline `style.cssText` palettes in this file are gone —
	// this draws with `.kapowx-toast` out of the token sheet, so the toast obeys
	// the same grammar as GCG's and meshsmith's (raised-2 surface, the repair
	// action FIRST and the only accent thing in it, `--gg-toast-duration`).
	_toast(msg, opts = {}) {
		// ASK-163 4s item 1b: one toast door (_toast.js) — dismisses itself at the token's 4 s unless it carries
		// an action, always closable by its ✕, a later toast never hidden by an earlier timer.
		return showToast(msg, opts);
	}

	// 2c staged 3D progress (ASK-080): a labelled progress bar inside the teleport
	// loader, driven by the shell GLB's real byte-progress. Lazily created on the
	// first onProgress event → if none arrive (total unknown), the spinner alone
	// shows (graceful). The dominant asset is the shell, so its bytes are the
	// meaningful signal; fixtures/finishing fold in as later phases when instrumented.
	_sceneProgress() {
		const loaderEl = this.refs?.loader;
		let handle = null;
		return {
			onAsset(ev) {
				if (!ev || !ev.total || !loaderEl) return;
				if (!handle) handle = loadingProgress(loaderEl, { stages: ["Loading shop…"] });
				handle.set(0, Math.min(ev.loaded / ev.total, 0.99));
			},
			done() { if (handle) { handle.set(0, 1); handle.done(); handle = null; } },
		};
	}

	// D-086 cl.3 — the THRESHOLD CARD (ASK-177 §10): `card` {name, accent, logoUrl} paints the shop's name + colour (null
	// clears it, undefined keeps what is painted); the bar shows only past 1 s (`data-slow`); `fadeMs` fades the card in.
	_showTeleportLoader(card, { fadeMs = 0 } = {}) {
		const loader = this.refs.loader, name = loader.querySelector("[data-threshold-name]"), art = loader.querySelector("[data-threshold-art]");
		if (card !== undefined) {
			loader.toggleAttribute("data-threshold", !!card?.name);
			loader.style.setProperty("--kx-threshold-accent", card?.accent || "");
			if (name) name.textContent = card?.name || "";
			if (art) { art.hidden = !card?.logoUrl; art.style.backgroundImage = card?.logoUrl ? `url("${encodeURI(card.logoUrl)}")` : ""; }
			loader.setAttribute("aria-label", card?.name ? `Entering ${card.name}` : "Loading");
		}
		loader.removeAttribute("data-slow"); clearTimeout(this._slowT);
		this._slowT = setTimeout(() => loader.setAttribute("data-slow", ""), 1000);
		if (fadeMs) loader.style.transitionDuration = `${fadeMs}ms`;   // kept until the hide (a later show must not clobber the fade)
		if (fadeMs && loader.hidden) { loader.classList.add('is-fading'); loader.hidden = false; requestAnimationFrame(() => loader.classList.remove('is-fading')); return; }
		loader.classList.remove('is-fading');
		loader.hidden = false;
	}

	_hideTeleportLoader({ immediate = false } = {}) {
		const loader = this.refs.loader;
		if (immediate) {
			loader.style.transitionDuration = "";
			loader.classList.remove('is-fading');
			loader.hidden = true;
			return;
		}
		// setTimeout is the fallback in case transitionend doesn't fire
		// (reduced-motion or paint-skip).
		let dismissed = false;
		const dismiss = () => {
			if (dismissed) return;
			dismissed = true;
			loader.hidden = true;
			loader.classList.remove('is-fading');
		};
		loader.style.transitionDuration = "";
		loader.classList.add('is-fading');
		loader.addEventListener('transitionend', dismiss, { once: true });
		setTimeout(dismiss, 1100);
	}

	// Listen for the runtime's `kapowx:capacityOverflow` window event (fired
	// by fixture builders when products exceed a fixture's render capacity)
	// and POST it to GCG so the curator can route the surplus down the
	// per-anchor overflow chain on its next allocation pass. Backstop only —
	// in normal operation the curator reads our published capacity and plans
	// without ever over-allocating.
	_installCapacityOverflowRelay() {
		if (typeof window === "undefined") return;
		window.addEventListener("kapowx:capacityOverflow", async (ev) => {
			const detail = ev?.detail;
			if (!detail?.anchorName || !Array.isArray(detail.surplusProductIds) || detail.surplusProductIds.length === 0) return;
			const gateway = this.config.gatewayConfig;
			// Relay the overflow to the shop the customer is ACTUALLY walking, not the gateway default.
			// `gatewayConfig.shopId` is the single build-env default (VITE_GATEWAY_SHOP_ID=demo-store); a
			// fixture overflows in the ACTIVE shop, so posting the surplus to the default mis-routes it —
			// on a stamped test-shop walk the surplus went to demo-store → 403 golden_shop_readonly (the
			// curator for the walked shop never saw it). Mirror `_emitTelemetry`: activeProjectId first.
			const shopId  = this.state.activeProjectId || gateway?.shopId;
			const baseUrl = gateway?.baseUrl;
			if (!shopId || !baseUrl) {
				console.warn("[overflow-relay] no active shopId/baseUrl — overflow event not relayed", detail);
				return;
			}
			const url = `${baseUrl.replace(/\/$/, "")}/admin/shops/${encodeURIComponent(shopId)}/capacity-overflow`;
			const headers = { "Content-Type": "application/json" };
			// E3 — fresh token per request; the gateway snapshot stales after 1h.
			const token = (await gateway?.getFreshIdToken?.().catch(() => null)) || gateway?.firebaseIdToken;
			if (token) headers["Authorization"] = `Bearer ${token}`;
			try {
				const resp = await fetch(url, {
					method: "POST",
					headers,
					body:   JSON.stringify({
						anchorName:        detail.anchorName,
						surplusProductIds: detail.surplusProductIds,
					}),
				});
				if (!resp.ok) {
					console.warn(`[overflow-relay] POST ${url} → ${resp.status}`);
				} else {
					console.log(`[overflow-relay] ${detail.anchorName}: ${detail.surplusProductIds.length} surplus id(s) relayed to GCG`);
				}
			} catch (err) {
				console.warn("[overflow-relay] fetch failed:", err);
			}
		});
	}

	// Telemetry-0 emitter (per DECISION-012, ASK-002). Fire-and-forget POST
	// to GCG's per-shop telemetry ingest. Caller passes eventType + payload;
	// timestamp + sessionId + auth attach here. Never awaited by the runtime
	// — telemetry must never block scene composition or interaction.
	//
	// Shop scoping: events emit against the active shop the customer is in
	// (or the gateway default for pre-navigation events like initial
	// `perf:page-load`). The endpoint requires a shopId in the URL even for
	// events that aren't intrinsically shop-scoped.
	_emitTelemetry(eventType, payload = {}, shopIdOverride) {
		const shopId = shopIdOverride
			|| this.state.activeProjectId
			|| this.config?.gatewayConfig?.shopId;
		if (!shopId) return;
		// The hub isn't a provisioned merchant shop — its /telemetry 404s. Skip it so
		// the landing console is clean (pre-existing; not a Chunk-3 regression).
		if (this.projectBootstraps?.[shopId]?.kind === "hub") return;
		const transport = makeTelemetryTransport(this.config, shopId);
		if (!transport) return;
		const event = {
			eventType,
			timestamp: Date.now(),
			sessionId: this.state.telemetrySessionId,
			payload,
		};
		// Fire-and-forget. Transport swallows non-2xx + network errors with a
		// console.warn. Telemetry-1 may swap this for buffered/flushed batching;
		// for now each event is its own 1-element POST per the ASK-002 spec.
		void transport.send([event]);
	}

	// Capture top-level browser errors + unhandled promise rejections and
	// route them through `/telemetry` as `runtime:error` events. GCG side
	// pipes these to GCP Error Reporting via Pino-structured Cloud Logging
	// (DECISION-012) so stack traces auto-group across sessions without an
	// extra SDK in the runtime bundle.
	_installRuntimeErrorRelay() {
		if (typeof window === "undefined") return;
		window.addEventListener("error", (ev) => {
			const err = ev?.error;
			this._emitTelemetry("runtime:error", {
				message: err?.message || ev?.message || "unknown error",
				stack:   err?.stack,
				url:     ev?.filename || window.location.href,
			});
		});
		window.addEventListener("unhandledrejection", (ev) => {
			const reason = ev?.reason;
			this._emitTelemetry("runtime:error", {
				message: reason?.message || String(reason) || "unhandled rejection",
				stack:   reason?.stack,
				url:     window.location.href,
			});
		});
	}

	// Fetches a specific shop's product catalog from GCG, caches by projectId
	// for the session. Multi-shop: each shop has its own feed URL
	// (`/api/kapowx/v1/shops/{shopId}/products`); we hit the one for the shop
	// the player is entering, not a single hard-coded one at boot.
	//
	// Returns the normalised product list (possibly empty). Never throws —
	// 404 / network failure resolves to `[]` (cached so we don't refetch),
	// and stock-honest fixture builders render empty geometry gracefully.
	//
	// shopId is the runtime projectId (e.g. "demo-store", "nocturn"). GCG
	// resolves either runtime_shop_id or its internal shop_id per the
	// 2026-05-17 mapping fix.
	async _fetchProductsForShop(shopId, { live = false, signal } = {}) {
		if (!shopId) return [];
		if (Array.isArray(this.state.productsByShop[shopId])) {
			return this.state.productsByShop[shopId];
		}
		const gateway = this.config.gatewayConfig;
		if (!gateway?.baseUrl) {
			console.warn(`[kapowx] No gateway configured — shop '${shopId}' will load with an empty catalog. Set VITE_GATEWAY_URL.`);
			this.state.productsByShop[shopId] = [];
			return this.state.productsByShop[shopId];
		}
		const url = `${gateway.baseUrl}/api/${gateway.apiNamespace || "kapowx/v1"}/shops/${encodeURIComponent(shopId)}/products`;
		console.log(`[kapowx] Fetching products for shop=${shopId}${live ? " (live — no-store)" : ""}`);
		try {
			// ASK-097 fix-1 — the merchant/Studio surface reads LIVE (bypass the feed's cache-control
			// max-age=300). Without this the panel could read a pre-backfill cached response
			// (products[] populated but assortment[]=[]) while Play rendered fresh products[] — STOCK
			// showed 0 despite a live pool. The CUSTOMER runtime read stays cached (live defaults false),
			// per D-058: assortment is live for the merchant surface only.
			const init = { credentials: "same-origin", signal };   // signal: the door prompt's prefetch, cancelled on leave (D-086 cl.3)
			if (live) init.cache = "no-store";
			const resp = await fetch(url, init);
			if (!resp.ok) {
				console.warn(`[kapowx] Products endpoint returned ${resp.status} for shop=${shopId} — caching empty`);
				this.state.productsByShop[shopId] = [];
				return this.state.productsByShop[shopId];
			}
			const json = await resp.json();
			// 🔴 ASK-137 F-115p.1 — THE CURRENCY RIDES THE FEED'S TOP LEVEL, and it was dropped here.
			//
			// Product LINES carry no currency; the feed does (`currency: "ZAR"`). So every price in the
			// customer chrome printed a bare number — measured on the plane (§83a): the item card read
			// "E2E · tin robot to sell / 24", which Vanessa reported as *"no price or product card"*,
			// and the comic hero panel has the same fault one line of markup away. A number is not a
			// price. Kept per shop, and pushed onto the stage so the lazy item hero can read it from
			// the object it already holds rather than reaching for a global.
			this.state.currencyByShop ||= {};
			if (json?.currency) {
				this.state.currencyByShop[shopId] = json.currency;
				if (this.stage && shopId === this.state.activeProjectId) this.stage.shopCurrency = json.currency;
			}
			const list = json?.products ?? json?.data?.products ?? null;
			if (!Array.isArray(list)) {
				console.warn(`[kapowx] Unexpected products response shape for shop=${shopId}`, json);
				this.state.productsByShop[shopId] = [];
				return this.state.productsByShop[shopId];
			}
			const normalised = list.map(_normalizeProduct);
			this.state.productsByShop[shopId] = normalised;
			// ASK-097 Step 2 — capture the additive resolved-assortment pool (present once GCG is
			// deployed to gcg-dev; absent → []). The runtime doesn't seat from this (it seats from
			// `products[].fixtureAssignments`); the Studio STOCK surfaces read it.
			const pool = json?.assortment ?? json?.data?.assortment ?? null;
			this.state.assortmentByShop[shopId] = Array.isArray(pool) ? pool : [];
			// ASK-127 (additive): `activePackId` rides the feed top level — ABSENT = base shop. Stored
			// as feed meta so the merchant Packs shelf reads its Active badge as a FEED FACT; nothing
			// on the customer path reads it, so a zero-pack shop is byte-for-byte unaffected.
			// ASK-139 F-139c — `commerce` ({adapter, checkout}) and `credits[]` ride the top level too (§10).
			// Kept as-read: absent stays null (no opinion), an empty credits list stays [] (none).
			(this.state.feedMetaByShop ||= {})[shopId] = {
				activePackId: json?.activePackId ?? null,
				shopId: typeof json?.shopId === "string" ? json.shopId : null,   // GCG's own id (demo-store → kapowx-coza)
				commerce: json?.commerce && typeof json.commerce === "object" ? json.commerce : null,
				credits: Array.isArray(json?.credits) ? json.credits : null,
				// coord ASK-180 §3.10 (K-COM, eComm rules 3/7) — WHO SELLS, server-held: GCG serves the `seller` block
				// (storeName · legalName · jurisdiction · supportEmail · returnsPolicyUrl · disclosure) on a GG-hosted
				// (`commerce.adapter gcg`) shop only; absent elsewhere = the client's own words, unchanged.
				seller: json?.seller && typeof json.seller === "object" ? json.seller : null,
			};
			console.log(`[kapowx] Cached ${normalised.length} products + ${this.state.assortmentByShop[shopId].length} assortment rows for shop=${shopId}`);
			return normalised;
		} catch (err) {
			if (signal?.aborted) return null;   // a cancelled prefetch caches nothing
			console.warn(`[kapowx] Products fetch failed for shop=${shopId}:`, err);
			this.state.productsByShop[shopId] = [];
			return this.state.productsByShop[shopId];
		}
	}

	// ASK-097 Step 2 — force-refresh a shop's feed (products + assortment) after a pool mutation.
	// Clears the session cache so `_fetchProductsForShop` re-hits GCG, then returns the fresh pool.
	// The Studio assortment browser calls this after add/remove so the cards + STOCK reflections
	// re-render truthfully from the live feed (never a local guess).
	// ASK-099 (last WYSIWYG gap): the fresh feed also re-seats the 3D SCENE in place — panel,
	// feed AND pixels stay one truth after Clear/carry-all. Signature-guarded stage-side, so the
	// settle loop's repeat refetches only rebuild when assignments actually changed.
	async refetchShopFeed(shopId) {
		if (!shopId) return [];
		await this.prefetchShopFeed(shopId);
		await this.rehydrateSeatedProducts(shopId);
		return this.state.assortmentByShop[shopId] || [];
	}

	// ASK-127 E4/F2 — the FETCH half of the refetch above, with NO scene mutation. A pack activation
	// changes the shop through two paths (this feed → seated props; /instances → fixtures), and doing
	// them one-after-the-other made the merchant watch two separate landings a network round-trip
	// apart. Splitting fetch from apply lets Studio pull both halves in parallel and apply them as one
	// settle. `refetchShopFeed` keeps its exact behaviour for every existing caller.
	async prefetchShopFeed(shopId) {
		if (!shopId) return [];
		delete this.state.productsByShop[shopId];
		delete this.state.assortmentByShop[shopId];
		if (this.state.feedMetaByShop) delete this.state.feedMetaByShop[shopId];   // ASK-127: stale activePackId must not outlive its feed
		await this._fetchProductsForShop(shopId, { live: true });   // ASK-097 fix-1: merchant surface reads live
		return this.state.assortmentByShop[shopId] || [];
	}

	// ASK-099 — re-seat the ACTIVE shop's 3D scene from its (already-cached) feed, in place: the
	// stage disposes every seated product instance and re-runs the one composer pipeline with the
	// fresh pool. No scene reload, camera untouched. Consumers: the merchant mutation refetch
	// (above); later, an idle-gated customer feed tick (ASK-080 etiquette — flagged follow-on).
	async rehydrateSeatedProducts(shopId) {
		if (!shopId || shopId !== this.state.activeProjectId) return 0;   // only the walked shop has a composed scene
		const products = this.state.productsByShop[shopId] || [];
		try { return (await this.stage?.rehydrateSeatedProducts?.(products)) ?? 0; }
		catch (err) { console.warn("[reseat] scene re-seat failed:", err?.message || err); return 0; }
	}

	/** F-139h — the status line follows the stage's contents, on every compose / re-seat / placed row. */
	_refreshStageStatus() {
		if (this.refs.stageStatus && this.stage?.sceneSummary) this.refs.stageStatus.textContent = this.stage.sceneSummary();
	}

	getRuntimeFixtures() {
		return Array.isArray(this.state.scene?.fixtures) ? this.state.scene.fixtures : [];
	}

	updateBoxHint(box) {
		if (!box) {
			this.refs.boxHint.hidden = true;
			return;
		}
		this.refs.boxHintName.textContent = box.name;
		this.refs.boxHint.hidden = false;
	}

	// ── Comic HUD ──────────────────────────────────────────────────────────────

	renderComicHud() {
		const selected = this.state.selectedComic;
		if (!selected?.product) {
			this.refs.comicHud.classList.remove("is-visible");
			this.refs.comicActions.hidden = true;
			if (this.refs.comicConnector) this.refs.comicConnector.style.opacity = "0";
			this.refs.heroPanel.classList.remove("is-open");
			this.state.heroPanelOpen = false;
			this.state.heroPanelCoverSlot = 'front';
			return;
		}
		const p = selected.product;
		this.refs.hudTitle.textContent = p.title || `Product ${p.id}`;
		this.refs.hudPrice.textContent = p.price || "";
		this.refs.hudPrice.hidden = !p.price;
		// Hero panel is the primary info surface — hide the floating pill and actions
		this.state.heroPanelOpen = true;
		this.state.heroPanelCoverSlot = 'front';
		this.renderHeroPanel(p);
		this.refs.heroPanel.classList.add("is-open");
		this._syncInspectInset();   // §94.7-1: the compass band is measured now it is surely laid out
		this.refs.comicActions.hidden = true;
	}

	/**
	 * 🔴 D-076 cl.1 — ONE PRODUCT PANEL, the same fields in the same slots for every type: media · title ·
	 * badges (type · stock · sold by) · price with currency · maker · scale note · Add to Cart · one
	 * action row · its verb line. A field a type lacks is ABSENT (hidden), never a placeholder — except
	 * the media slot of a 3D model, which shows the brand's 3D mark rather than "No image" (cl.4: the
	 * hero image replaces it as soon as the feed carries one).
	 */
	renderHeroPanel(product) {
		const r = this.refs;
		const kind = this.state.selectedComic?.kind || this.stage?.activeFixtureHero?.kind || null;
		const is3d = kind === "item";
		r.heroPanel.dataset.heroPanelKind = is3d ? "3d" : "print";
		r.heroPanelTitle.textContent = product.title || `Product ${product.id}`;

		const formatLabels = { modern: 'Modern', bronze: 'Bronze Age', silver: 'Silver Age', golden: 'Golden Age' };
		const typeName = String(product.type || product.kapowxProductType || "").replace(/[-_]+/g, " ").trim();
		const badges = [];
		if (typeName) badges.push(`<span class="kapowx-store__hero-panel-badge kapowx-store__hero-panel-badge--format" data-badge="type">${escapeHtml(typeName.charAt(0).toUpperCase() + typeName.slice(1))}</span>`);
		if (product.format && formatLabels[product.format]) {
			badges.push(`<span class="kapowx-store__hero-panel-badge kapowx-store__hero-panel-badge--format">${escapeHtml(formatLabels[product.format])}</span>`);
		}
		// The feed names stock `availability` ("instock"/"outofstock"); `stockStatus` is the legacy WP
		// name. Reading only the legacy one rendered every feed product "In Stock" whatever it said.
		const inStock = (product.availability ?? product.stockStatus) !== 'outofstock';
		badges.push(`<span class="kapowx-store__hero-panel-badge kapowx-store__hero-panel-badge--${inStock ? 'instock' : 'outofstock'}" data-badge="stock">${inStock ? 'In Stock' : 'Out of Stock'}</span>`);
		// M5 C9 — merchant identity badge (eComm rule 3 + DECISION-030 §5.3).
		const sellerOfRecord = this._resolveSellerOfRecord();
		badges.push(`<span class="kapowx-store__hero-panel-badge kapowx-store__hero-panel-badge--seller" data-badge="seller" title="Sold by ${escapeAttribute(sellerOfRecord.legalName)} (${escapeAttribute(sellerOfRecord.jurisdiction)})">Sold by ${escapeHtml(sellerOfRecord.legalName)}</span>`);
		r.heroPanelBadges.innerHTML = badges.join('');

		r.heroPanelPrice.textContent = this._money(product);
		r.heroPanelPrice.hidden = !product.price;
		// F-111c — the MAKER is a person, never the machine identity; absent means absent.
		const maker = product.contributorDisplayName || product.submittedByDisplayName || product.maker || "";
		r.heroPanelMaker.textContent = maker ? `by ${maker}` : "";
		r.heroPanelMaker.hidden = !maker;
		// D-074 — say so when what she is holding is not actual size.
		const scale = Number(product.displayScale);
		const scaled = Number.isFinite(scale) && scale > 0 && Math.abs(scale - 1) > 1e-6 ? +scale.toFixed(2) : null;
		r.heroPanelScale.textContent = scaled ? `Shown at ${scaled}× actual size` : "";
		r.heroPanelScale.hidden = !scaled;

		// cl.3 — ONE ACTION ROW: the universal verb, then the type's own, same order every time; the
		// verb line below lists exactly these, in this order, after the two gestures.
		// card 123qy9rtj5h — once THIS item is in the basket the universal verb reads "Close": it still puts the
		// display copy back on its seat, but the word no longer reads as taking it out of the basket.
		const kept = this._inBasket(product);
		const verbs = [["back", "R", kept ? "Close" : "Put back"]];
		if (!is3d) verbs.push(["flip", "F", "Flip"]);
		if (!is3d && product.hasInterior) verbs.push(["open", "O", "Open"]);
		r.heroPanelActions.innerHTML = verbs.map(([v, k, label]) =>
			`<button type="button" class="kapowx-store__hero-panel-btn kapowx-store__hero-panel-btn--${v}" data-hero-panel-verb="${v}" data-hero-panel-${v}>${label} <span class="kapowx-store__hero-key">${k}</span></button>`).join("");
		const extra = kind === "vinyl" ? " · <kbd>←→</kbd> Next" : "";
		// ASK-139 F-139c — on a touch device the verb line is gestures, not keys (there are no keys).
		r.heroPanelHint.innerHTML = this._touchFirst()
			? `Drag to turn · Pinch to zoom · Swipe the sheet down to ${kept ? "close — it stays in your basket" : "put back"}`
			: `Drag to turn · Scroll to zoom · ${verbs.map(([, k, label]) => `<kbd>${k}</kbd> ${label}`).join(" · ")}${extra}`;
		if (r.heroPanelCheckoutNote) r.heroPanelCheckoutNote.hidden = !this._checkoutClosed();
		r.heroPanelHint.classList.remove("is-faded");
		r.heroPanel.querySelector("[data-hero-panel-close]")?.setAttribute("aria-label", kept ? "Close — it stays in your basket" : "Put back");

		const coverUrl = this.state.heroPanelCoverSlot === 'back' ? (product.backImage || '') : this._panelMediaUrl(product);
		this._setPanelCoverImage(coverUrl);

		const thumbs = [];
		if (product.frontImage || product.image) {
			const url = product.frontImage || product.image;
			const active = this.state.heroPanelCoverSlot !== 'back';
			thumbs.push(`<img class="kapowx-store__hero-panel-thumb${active ? ' is-active' : ''}" src="${escapeAttribute(url)}" alt="Front" data-hero-panel-thumb="front">`);
		}
		if (product.backImage) {
			const active = this.state.heroPanelCoverSlot === 'back';
			thumbs.push(`<img class="kapowx-store__hero-panel-thumb${active ? ' is-active' : ''}" src="${escapeAttribute(product.backImage)}" alt="Back" data-hero-panel-thumb="back">`);
		}
		r.heroPanelThumbs.innerHTML = thumbs.join('');
		r.heroPanelThumbs.hidden = thumbs.length === 0;
	}

	/** The media slot's picture: the merchant's own front image always wins; a library model's stamped
	 *  hero image fills in when she has none (D-076 cl.4). */
	_panelMediaUrl(p) {
		return p.frontImage || p.image || p.heroImageUrl || '';
	}

	_setPanelCoverImage(url) {
		const r = this.refs, is3d = r.heroPanel.dataset.heroPanelKind === "3d";
		r.heroPanelCover.hidden = !url;
		if (url) r.heroPanelCover.src = url;
		// Never "No image" for a 3D model — it HAS an image, it is the thing in her hands.
		r.heroPanelCoverEmpty.hidden = !!url || is3d;
		r.heroPanelMedia3d.hidden = !!url || !is3d;
		// ASK-163 4s item 2 (WoW §14.5 product select/inspect): a skeleton in the slot until the picture has
		// loaded — never a half-painted download; a picture that fails says "No image" (or the 3D model shows).
		const slot = r.heroPanelCover.parentElement;
		if (url) imageSkeleton(slot, r.heroPanelCover, { onError: () => {
			if (r.heroPanelCover.getAttribute("src") !== url) return;
			r.heroPanelCover.hidden = true;
			r.heroPanelCoverEmpty.hidden = is3d;
			r.heroPanelMedia3d.hidden = !is3d;
		} });
		else { slot.querySelector(":scope > .kapowx-loading-skeleton--media")?.remove(); r.heroPanelCover.style.opacity = ""; }
	}

	_setPanelCoverSlot(slot) {
		const p = this.state.selectedComic?.product;
		if (!p) return;
		this.state.heroPanelCoverSlot = slot;
		const url = slot === 'back' ? (p.backImage || '') : this._panelMediaUrl(p);
		this._setPanelCoverImage(url);
		this.refs.heroPanelThumbs.querySelectorAll("[data-hero-panel-thumb]").forEach((el) => {
			el.classList.toggle("is-active", el.dataset.heroPanelThumb === slot);
		});
	}

	_updateComicHudPos(pos) {
		const hud = this.refs.comicHud;
		const svg = this.refs.comicConnector;
		if (this.state.heroPanelOpen || !pos || !this.state.selectedComic?.product) {
			hud.classList.remove("is-visible");
			if (svg) svg.style.opacity = "0";
			return;
		}
		// Offset card to the right (or left) of the comic's screen centre
		const PAD = 16;
		const hudW = 200;
		const hudH = 60;
		let x = pos.x + 90;
		let y = pos.y - hudH * 0.5;
		const flipLeft = x + hudW + PAD > window.innerWidth;
		if (flipLeft) x = pos.x - hudW - 90;
		x = Math.max(PAD, Math.min(window.innerWidth  - hudW - PAD, x));
		y = Math.max(PAD, Math.min(window.innerHeight - hudH - PAD, y));
		hud.style.transform = `translate(${Math.round(x)}px, ${Math.round(y)}px)`;
		hud.classList.add("is-visible");

		// Draw connector line from comic centre to nearest pill edge
		if (svg) {
			const line = svg.querySelector("line");
			if (line) {
				const cardCentreY = Math.round(y + hudH * 0.5);
				const toX = flipLeft ? Math.round(x + hudW) : Math.round(x);
				line.setAttribute("x1", Math.round(pos.x));
				line.setAttribute("y1", Math.round(pos.y));
				line.setAttribute("x2", toX);
				line.setAttribute("y2", cardCentreY);
			}
			svg.style.opacity = "1";
		}
	}

	// ── Cart ───────────────────────────────────────────────────────────────────
	// All persistence + network goes through this.cartService — see
	// cart-service.js. The service emits onChange after every mutation,
	// which triggers renderCartCount + renderCartPanel from the constructor's
	// subscription. These methods are thin: kick off the op + flash the UI.

	/** A price a shopper can read: the line's amount with the shop's currency (F-115p.1). The line's
	 *  own `currency` wins if it ever carries one, then the shop's, then the amount alone — stating a
	 *  currency we do not have would be worse than stating none. */
	_money(product) {
		const price = product?.price;
		if (price == null || price === "") return "";
		const cur = product?.currency || product?.currencyCode
			|| this.state.currencyByShop?.[this.state.activeProjectId] || "";
		return cur ? `${cur} ${price}` : String(price);
	}

	/**
	 * 🔴 D-075 clause 6 — ONE CART DOOR, and it takes a product.
	 *
	 * `addSelectedComicToCart` read `state.selectedComic`, so the only thing that could reach the cart
	 * was a comic the longbox had selected. The 3D inspect view has an Add to Cart too, and the ruling
	 * is explicit that it is the SAME door — "no second commerce path". So the body moves here and the
	 * comic entry point becomes one line that names its own subject.
	 */
	async addProductToCart(product, triggerEl = null) {
		if (!product) return;
		const addBtn = triggerEl
			|| this.refs.heroPanel?.querySelector("[data-hero-panel-add]")
			|| this.root.querySelector("[data-runtime-add-selected]");
		// §2.18jr (WoW §14.5 add-to-cart = OPTIMISTIC): the button says Added and the basket counts it NOW; a refusal undoes both
		const flash = this._flashCartAdded(addBtn);
		const result = await this.cartService.addItem(product);
		if (!result.ok) {
			flash.revert();
			console.warn("[kapowx] add-to-cart failed:", result.error);
			// S4 (123qy9rujru): a definitive refusal (412/409) says its reason; only network/5xx says "try again"
			this._toastCartError(cartRefusal(result).message);
			return;
		}
		this._syncKeptVerb(product);   // the verb now reads Close (123qy9rtj5h)
		this._emitTelemetry("cart:add", {
			shopId:      this.state.activeProjectId || null,
			productId:   product.id || product.kapowxProductId || null,
			productType: product.kapowxProductType || product.product_type || null,
			cartCount:   this.cartService?.count ?? null,
		});
	}

	/** After Add: the panel's universal verb, its close button and the verb line say Close — never Put back (123qy9rtj5h). */
	_syncKeptVerb(product) {
		const p = this.refs.heroPanel;
		if (!p || !this._inBasket(product)) return;
		const b = p.querySelector("[data-hero-panel-back]");
		if (b?.firstChild) b.firstChild.textContent = "Close ";
		p.querySelector("[data-hero-panel-close]")?.setAttribute("aria-label", "Close — it stays in your basket");
		const h = this.refs.heroPanelHint;
		if (h) h.innerHTML = h.innerHTML.replace("Swipe the sheet down to put back", "Swipe the sheet down to close — it stays in your basket").replace("</kbd> Put back", "</kbd> Close");
	}

	_inBasket(product) {
		const id = product?.kapowxProductId ?? product?.id;
		return id != null && this.cartService.lines.some((l) => String(l.kapowxProductId ?? l.id) === String(id));
	}

	async addSelectedComicToCart() {
		// The comic entry point, naming its own subject. The work is `addProductToCart` — one door.
		return this.addProductToCart(this.state.selectedComic?.product || null);
	}

	// Brief non-blocking error toast for cart operations.
	_toastCartError(msg) {
		const t = document.createElement("div");
		t.className = "kapowx-toast kapowx-toast--error is-visible";
		t.textContent = msg;
		document.body.appendChild(t);
		// The dwell is the token, not a number nobody can find (§6: toasts are 4 s).
		const ms = parseInt(getComputedStyle(document.documentElement).getPropertyValue("--gg-toast-duration"), 10);
		setTimeout(() => t.remove(), Number.isFinite(ms) && ms > 0 ? ms : 4000);
	}

	/** ASK-139 D6 — the shop's feed says checkout is closed (alpha): the cart is real, checkout is not.
	 *  ASK-145 §2 — and on a plane in catalog mode (production) it is closed on EVERY shop, whatever
	 *  the feed says: the plane's flag can only close checkout, never open it. */
	_checkoutClosed(shopId = this.state.activeProjectId) {
		if (checkoutForcedClosed()) return true;
		return checkoutStopperShown({ commerce: this.state.feedMetaByShop?.[shopId]?.commerce ?? null });
	}

	/**
	 * ASK-139 F-139c (5) — bind the cart backend the SHOP names (feed `commerce.adapter`). `gcg` → the
	 * GCG cart keyed by this shop; absent → the Kapow Medusa dev store keeps the boot default, every
	 * other shop gets its own GCG cart (`cartTransportKey`). The Medusa dev default and its
	 * substitute-the-first-product fallback are unreachable from any shop but its own, so what a
	 * shopper adds is what the cart holds.
	 */
	async _bindShopCommerce(shopId) {
		const meta = this.state.feedMetaByShop?.[shopId] ?? null;
		const commerce = meta?.commerce ?? null;
		const key = cartTransportKey(shopId, commerce, meta?.shopId ?? null);
		if (key !== this._cartTransportKey) {
			this._cartTransportKey = key;
			this._cartHydrated = true;
			await this.cartService.useTransport(cartTransportFor(this.config, shopId, commerce, meta?.shopId ?? null));
		} else if (!this._cartHydrated) {
			this._cartHydrated = true;
			await this.cartService.hydrate();
		}
		this.renderCartPanel();
	}

	async checkout() {
		if (this._checkoutClosed()) return;   // the stopper says when; it is not a dead button
		if (!this.cartService.supportsCheckout || !this.state.cart.length) return;
		// AAA purchase-flow feedback: the button goes busy INSTANTLY (the
		// session setup + redirect takes seconds) — and the disable doubles as
		// double-submit protection (a double-click must never create two
		// payment sessions). Reset on any failure path; success navigates away.
		if (this.state.checkoutBusy) return;
		this.state.checkoutBusy = true;
		this.renderCartPanel();
		// Seamless return (AAA): remember WHERE the customer is shopping —
		// Paystack returns to the bare callback URL, which boots at the hub;
		// the return handler navigates back here before confirming.
		try { sessionStorage.setItem("kapowx_return_shop", this.state.activeProjectId || ""); } catch { /* noop */ }

		// Telemetry-0 M5 C11: `checkout:start` business event — fires when
		// customer initiates checkout. Hand-off to merchant's commerce backend
		// happens after this point per DECISION-031 §3.3 + DECISION-035 (Type A
		// merchant's Woo / Type B GG-hosted Saleor; payment provider per
		// merchant's choice). Fires regardless of subsequent embedded vs
		// redirect path.
		this._emitTelemetry("checkout:start", {
			shopId:    this.state.activeProjectId || null,
			cartCount: this.cartService?.count ?? null,
		});

		// coord ASK-180 §3.10 item 1 (K-COM, D-092) — a GG-hosted shop (`commerce.adapter gcg`) checks out through the PAY
		// SHEET: receipt email · where it goes · a note, the disclosure above Pay, then Paystack's hosted page on the
		// merchant's own account. The sheet is a lazy chunk (ASK-159 §2.18z: the customer-initial budget).
		if (this._shopIsGgHosted()) {
			let Overlay = null;
			try { Overlay = (await import("./checkout-overlay.js")).CheckoutOverlay; }
			catch (err) { console.warn("[kapowx] the pay sheet did not load:", err?.message || err); }
			this.state.checkoutBusy = false;
			this.renderCartPanel();
			if (Overlay) this._openPaySheet(Overlay);
			else this._toastCartError("Checkout couldn't open — please try again in a moment.");
			return;
		}

		// Every other backend (the Kapow Medusa dev store; a WooCommerce-plugin shop): the redirect handoff its own
		// transport opens. (The iter-4 embedded Woo form — Cash on Delivery / Bank Transfer — went with K-COM.)
		const result = await this.cartService.checkout();
		if (!result.ok || !result.url) {
			console.warn("[kapowx] checkout failed:", result.error || "no url");
			this.state.checkoutBusy = false;
			this.renderCartPanel();
			return;
		}
		// Same-tab navigation matches spec §6.6 (kind: 'redirect') and avoids
		// popup blockers.
		window.location.href = result.url;
	}

	/** K-COM — the shop's feed names GCG as its order ledger (Type B, D-092). */
	_shopIsGgHosted(shopId = this.state.activeProjectId) {
		return this.state.feedMetaByShop?.[shopId]?.commerce?.adapter === "gcg";
	}

	/** K-COM — where Paystack sends the shopper back: this shop, marked as a GG-hosted return (Paystack appends
	 *  `&trxref=&reference=`). https only (the gateway refuses anything else as `bad_callback_url`). */
	_payReturnUrl(shopId = this.state.activeProjectId) {
		try {
			const u = new URL(window.location.origin + "/");
			if (u.protocol !== "https:") return null;
			u.searchParams.set("shop", shopId);
			u.searchParams.set("kxpay", "1");
			return u.toString();
		} catch { return null; }
	}

	_openPaySheet(CheckoutOverlay) {
		if (!this.refs.checkoutMount) return;
		const seller = this._resolveSellerOfRecord();
		const { lines, totalText } = this._basketSummary();
		const args = { seller, lines, totalText, email: this.config?.customerEmail || "" };
		if (!this._checkoutOverlay) {
			this._checkoutOverlay = new CheckoutOverlay({
				host: this.refs.checkoutMount,
				cartService: this.cartService,
				callbackUrl: this._payReturnUrl(),
				...args,
				onRedirect: (url) => {
					this._emitTelemetry("checkout:redirect", { shopId: this.state.activeProjectId || null, provider: "paystack" });
					window.location.assign(url);
				},
			});
		} else {
			this._checkoutOverlay.update(args);
			this._checkoutOverlay.callbackUrl = this._payReturnUrl();
		}
		this.state.cartOpen = false;
		this.renderCartPanel();
		this._checkoutOverlay.open();
	}

	/** The basket as the cart panel prints it: lines with their price text, and the total only when every line has one. */
	_basketSummary() {
		const lines = this.state.cart || [];
		const cur = this.state.currencyByShop?.[this.state.activeProjectId] || "";
		const amountOf = (v) => { const n = Number(String(v ?? "").replace(/[^0-9.\-]/g, "")); return Number.isFinite(n) && String(v ?? "").trim() !== "" ? n : null; };
		const priceText = (v) => { const n = amountOf(v); return n == null ? "—" : (/^[0-9.\s]+$/.test(String(v).trim()) && cur ? `${cur} ${v}` : String(v)); };
		const amounts = lines.map((l) => amountOf(l.price));
		const total = lines.length && amounts.every((a) => a != null) ? amounts.reduce((t, a, i) => t + a * (lines[i].quantity || 1), 0) : null;
		return {
			lines: lines.map((l) => ({ title: l.title, quantity: l.quantity || 1, price: priceText(l.price) })),
			totalText: total != null ? (cur ? `${cur} ${total.toFixed(2)}` : total.toFixed(2)) : "",
		};
	}

	// M5 C9 — seller-of-record resolver per DECISION-030 §11. Returns the
	// entity legally selling the current transaction. Today always resolves
	// to the merchant (per DECISION-035 + DECISION-030 §3 Posture A for
	// domestic; cross-border MoR-partner branch lives behind a future flag
	// when DECISION-033 ratifies). Customer-facing surfaces (hero panel,
	// cart, checkout) read .legalName + .disclosureText from this resolver,
	// NEVER hardcode "[Merchant]" — per DECISION-031 §11 disclosure-binding
	// guidance.
	_resolveSellerOfRecord() {
		const shop = this.config?.shops?.[this.state.activeProjectId]
		          ?? this.config?.shops?.[this.config?.gatewayConfig?.shopId];
		const commerce = shop?.commerce ?? {};
		// K-COM (coord ASK-180 §3.10, eComm rules 3/7) — a GG-hosted shop's seller is SERVER-HELD: the feed's `seller`
		// block names the merchant's legal name and carries the disclosure sentence GCG also snapshots on the order.
		const served = this.state?.feedMetaByShop?.[this.state.activeProjectId]?.seller ?? null;
		if (served?.storeName) {
			return {
				role: "merchant",
				legalName: served.storeName,
				jurisdiction: served.jurisdiction || "ZA",
				disclosureText: served.disclosure || `You are purchasing from ${served.storeName}.`,
				supportEmail: served.supportEmail ?? null,
				returnsPolicyUrl: served.returnsPolicyUrl ?? null,
				served: true,
			};
		}
		const legalName = commerce.storeName
		               ?? shop?.title
		               ?? "this shop";
		const jurisdiction = commerce.jurisdiction ?? "ZA";  // beta default per DECISION-035
		// eComm rule 7 disclosure copy (DECISION-030 §5.7); template substitutes
		// resolved legalName so the customer sees the actual merchant identity,
		// not the placeholder. Final legally-blessed wording is on Shaun's
		// parallel legal track per DECISION-030 §8 (this is the working
		// in-code draft per DECISION-035 §8 + working-disclosure-copy carve-out).
		const disclosureText =
			`You are purchasing from ${legalName}. ` +
			`Payments, fulfilment, returns, and support are handled by ${legalName}. ` +
			`Storefront powered by Galaxy Galleria.`;
		return {
			role: "merchant",
			legalName,
			jurisdiction,
			disclosureText,
		};
	}

	async _handlePaystackReturn() {
		let ref = null, ggHosted = false, payShop = null;
		try {
			const params = new URLSearchParams(window.location.search);
			ref = params.get("reference") || params.get("trxref");
			ggHosted = params.get("kxpay") === "1";
			payShop = params.get("shop");
		} catch { return; }
		if (!ref) return;
		// Clean the URL immediately so a refresh doesn't re-trigger completion (the shop stays: it is where she was).
		try {
			const url = new URL(window.location.href);
			url.searchParams.delete("reference"); url.searchParams.delete("trxref"); url.searchParams.delete("kxpay");
			history.replaceState(null, "", url.toString());
		} catch { /* noop */ }
		if (ggHosted && payShop) return this._completeGgHostedReturn(payShop, ref);
		const transport = this.cartService?.transport;
		if (typeof transport?.completeAfterReturn !== "function") return;
		// Seamless return: the bare callback URL boots at the hub — put the
		// customer back in the shop they were buying from BEFORE showing the
		// confirmation, so "Continue shopping" is literal. Wait for the initial
		// scene boot, then portal-hop if needed.
		let returnShop = null;
		try { returnShop = sessionStorage.getItem("kapowx_return_shop") || null; sessionStorage.removeItem("kapowx_return_shop"); } catch { /* noop */ }
		const result = await transport.completeAfterReturn();
		if (returnShop && returnShop !== this.state.activeProjectId) {
			for (let i = 0; i < 100 && !this.projectBootstraps?.[returnShop]; i++) {
				await new Promise((r) => setTimeout(r, 100));   // wait for boot config
			}
			if (this.projectBootstraps?.[returnShop]) {
				try { await this.navigateToProject(returnShop); } catch (err) { console.warn("[kapowx] return-shop hop failed:", err); }
			}
		}
		if (result.ok && result.order) {
			// Tag the event with the shop the PURCHASE belongs to — at return-
			// boot time activeProjectId starts null (Shaun's dashboard showed
			// Completes=0 while Starts=3: the event was firing shopId-less
			// before any shop was active).
			this._emitTelemetry("checkout:complete", {
				shopId: returnShop || this.state.activeProjectId || null,
				source: "redirect",
				orderId: result.order.orderId,
			}, returnShop || undefined);
			this.cartService.markCleared();
			this._showOrderConfirmation(result.order);
		} else {
			// Payment may still be settling (webhook race) — honest holding copy;
			// the receipt email is the durable confirmation either way.
			this._showOrderConfirmation(null, ref);
		}
	}

	/**
	 * coord ASK-180 §3.10 item 2 (K-COM) — back from Paystack on a GG-hosted shop. The callback (`_payReturnUrl`) deep
	 * links THIS shop, so the shopper lands where she paid; once the shop's own GCG cart is bound, GCG reads the order
	 * that reference names — settling a still-pending one by Paystack verify first — so the page never says "paid" on the
	 * redirect alone. Bounded: the shop's compose decides how long the bind takes, not a fixed sleep.
	 */
	async _completeGgHostedReturn(shopId, reference) {
		// called from the constructor, before `this.state` exists — the first reads must tolerate that
		const bound = () => this.state?.activeProjectId === shopId && this._cartTransportKey === `gcg:${shopId}` && typeof this.cartService?.transport?.completeAfterReturn === "function";
		for (let i = 0; i < 400 && !bound(); i++) await new Promise((r) => setTimeout(r, 300));
		if (!bound()) { this._showOrderConfirmation(null, reference); return; }
		// coord ASK-180 §3.11 item 5 (K-COM D4) — the confirmation waits for the shop's compose to SETTLE: the cart binds while
		// the shell is still composing ("Loading shop…" under the card, §3.10a frame b7). The navigation's own flag and the
		// stage's compose counter say when the shop is drawn; bounded like the bind, so a stuck compose still confirms.
		const settled = () => !this.state?.isLoading && !(this.stage?._composeDepth > 0);
		for (let i = 0; i < 200 && !settled(); i++) await new Promise((r) => setTimeout(r, 300));
		if (typeof this.stage?._composeSettled === "function") await Promise.race([this.stage._composeSettled(), new Promise((r) => setTimeout(r, 1000))]);
		let result = await this.cartService.transport.completeAfterReturn(reference);
		// a webhook can trail the redirect by a moment — one bounded re-read before the holding copy
		if (result.ok && result.order?.status === "pending") {
			await new Promise((r) => setTimeout(r, 2500));
			result = await this.cartService.transport.completeAfterReturn(reference);
		}
		if (result.ok && result.order) {
			if (result.order.status === "paid" || result.order.status === "fulfilled") {
				this._emitTelemetry("checkout:complete", { shopId, source: "redirect", orderId: result.order.orderId }, shopId);
				await this.cartService.hydrate();   // the bought lines left the basket when the payment settled (GCG)
			}
			this._showOrderConfirmation(result.order);
		} else {
			this._showOrderConfirmation(null, reference);
		}
	}

	// The order confirmation (redirect path). Plain DOM over the 3D canvas; dismiss returns the customer to the shop.
	// K-COM (eComm rule 3): it names the SELLER, the order's reference and the receipt; a still-pending order says so.
	_showOrderConfirmation(order, pendingRef = null) {
		document.querySelector("[data-order-confirmation]")?.remove();
		const div = document.createElement("div");
		div.className = "kapowx-overlay-surface";
		div.setAttribute("data-order-confirmation", "");
		const seller = order?.sellerOfRecord?.storeName || "the shop";
		const money = (m) => (m ? `${m.currencyCode === "ZAR" ? "R" : (m.currencyCode || "") + " "}${(Number(m.amount) / 100).toFixed(2)}` : "");
		const ref = order?.reference || (order?.orderId ? `${order.orderId.slice(0, 14)}…` : pendingRef);
		const settled = order && (order.status === "paid" || order.status === "fulfilled");
		const pending = !order || order.status === "pending";
		const links = [
			order?.supportEmail ? `<a href="mailto:${escapeAttribute(order.supportEmail)}">Contact ${escapeHtml(seller)}</a>` : "",
			order?.returnsPolicyUrl ? `<a href="${escapeAttribute(order.returnsPolicyUrl)}" target="_blank" rel="noopener">Returns policy</a>` : "",
		].filter(Boolean).join(" · ");
		const canList = this._shopIsGgHosted() && typeof this.cartService?.listOrders === "function";
		div.innerHTML = `
			<div class="kapowx-overlay-card" data-order-confirm-status="${escapeAttribute(order?.status || "unknown")}">
				<div class="kapowx-overlay-mark">${settled ? kxIcon("check", "mark") : kxIcon("clock", "mark")}</div>
				<h2>${settled ? "Order placed!" : order?.status === "cancelled" ? "Order cancelled" : "Payment processing…"}</h2>
				<p>
					${settled
						? `Thanks — your order (${escapeHtml(money(order.total))}) with <strong data-order-confirm-seller>${escapeHtml(seller)}</strong> is confirmed. Reference <strong data-order-confirm-ref>${escapeHtml(ref || "")}</strong>. A receipt is on its way to your email.`
						: pending
							? `Your payment${order ? ` to <strong data-order-confirm-seller>${escapeHtml(seller)}</strong>` : ""} (ref <strong data-order-confirm-ref>${escapeHtml(ref || "")}</strong>) is being confirmed. A receipt is on its way to your email once it is — no need to pay again.`
							: `Order <strong data-order-confirm-ref>${escapeHtml(ref || "")}</strong> with <strong data-order-confirm-seller>${escapeHtml(seller)}</strong> was cancelled.`}
				</p>
				${order ? `<p class="kapowx-overlay-fineprint">Payments, delivery, returns and support are handled by ${escapeHtml(seller)}.${links ? ` ${links}` : ""}</p>` : ""}
				<div class="kapowx-overlay-actions">
					${canList ? `<button type="button" data-order-confirm-orders class="kapowx-overlay-btn kapowx-overlay-btn--quiet">Your orders</button>` : ""}
					<button type="button" data-order-confirm-close class="kapowx-overlay-btn">Continue shopping</button>
				</div>
			</div>`;
		document.body.appendChild(div);
		div.querySelector("[data-order-confirm-close]")?.addEventListener("click", () => div.remove());
		div.querySelector("[data-order-confirm-orders]")?.addEventListener("click", () => { div.remove(); void this._showMyOrders(); });
	}

	/** K-COM item 2 — the shopper's orders in THIS shop (OrderPort.listOrders → `GET /sessions/:id/orders`): reference ·
	 *  date · total · the status in words · the seller. An overlay over the shop, like the confirmation. */
	async _showMyOrders() {
		document.querySelector("[data-my-orders]")?.remove();
		const div = document.createElement("div");
		div.className = "kapowx-overlay-surface";
		div.setAttribute("data-my-orders", "loading");
		div.innerHTML = `<div class="kapowx-overlay-card kapowx-overlay-card--list"><h2>Your orders</h2><p class="kapowx-overlay-fineprint"><span class="kapowx-store__btn-spinner" aria-hidden="true"></span> Reading your orders…</p></div>`;
		document.body.appendChild(div);
		const r = await this.cartService.listOrders();
		const items = (r.items || []).slice().sort((a, b) => String(b.placedAt).localeCompare(String(a.placedAt)));
		const rows = items.map((o) => `
			<li class="kapowx-orders__row" data-my-order-row data-status="${escapeAttribute(o.status)}" data-reference="${escapeAttribute(o.reference || "")}">
				<span class="kapowx-orders__ref">${escapeHtml(o.reference || o.orderId.slice(0, 8))}</span>
				<span class="kapowx-orders__when">${escapeHtml(new Date(o.placedAt).toLocaleDateString())}</span>
				<span class="kapowx-orders__total">${escapeHtml(formatPortMoney(o.total))}</span>
				<span class="kapowx-orders__status kapowx-orders__status--${escapeAttribute(o.status)}">${escapeHtml(orderStatusWord(o.status))}</span>
				<span class="kapowx-orders__seller">Sold by ${escapeHtml(o.sellerOfRecord?.storeName || "")}</span>
			</li>`).join("");
		div.setAttribute("data-my-orders", r.ok ? "ready" : "failed");
		div.innerHTML = `
			<div class="kapowx-overlay-card kapowx-overlay-card--list">
				<h2>Your orders</h2>
				${!r.ok ? `<p>Your orders couldn't be read just now — please try again in a moment.</p>`
					: items.length ? `<ul class="kapowx-orders">${rows}</ul>` : `<p>No orders in this shop yet.</p>`}
				<div class="kapowx-overlay-actions"><button type="button" data-my-orders-close class="kapowx-overlay-btn">Close</button></div>
			</div>`;
		div.querySelector("[data-my-orders-close]")?.addEventListener("click", () => div.remove());
	}

	_onCheckoutComplete() {
		// Telemetry-0 M5 C11: `checkout:complete` business event — fires on
		// successful checkout completion (embedded path; webhook-driven path
		// for redirect-checkout is GCG-side per DECISION-035 multi-provider
		// webhook dispatch). Per DECISION-031 §3.3 customer-side detection
		// is the customer-return-from-checkout signal; GG's view of completion
		// is fire-and-forget; backend remains source of truth.
		this._emitTelemetry("checkout:complete", {
			shopId: this.state.activeProjectId || null,
			source: "embedded",
		});

		// Order submitted successfully — server already cleared the shadow
		// cart, sync the local mirror so the cart badge resets to 0.
		this.cartService.markCleared();
	}

	/** The press's optimistic half: "Added" on the button at once; `revert()` puts the label back when the add is refused. */
	_flashCartAdded(btn) {
		const none = { revert() {} };
		if (!btn) return none;
		// Find the text node (last child) to avoid clobbering the SVG icon.
		const textNode = [...btn.childNodes].find((n) => n.nodeType === Node.TEXT_NODE && n.textContent.trim());
		if (!textNode) return none;
		const original = textNode.textContent;
		textNode.textContent = " Added";
		btn.disabled = true;
		const restore = () => { textNode.textContent = original; btn.disabled = false; };
		const t = setTimeout(restore, 1500);
		return { revert() { clearTimeout(t); restore(); } };
	}

	renderCartCount() {
		const count = this.cartService.count;
		// 🔴 ASK-129 F-107a — THE BADGE IS CAPPED SO THE BUTTON'S WIDTH IS BOUNDED.
		// The mode switch reserves a fixed gutter for this control (`--gg-cart-gutter`), and a
		// reserve is only a guarantee if the thing it reserves for cannot grow past it. 99+ is the
		// badge convention everywhere it matters; the exact number lives in the open cart.
		this.refs.cartCount.textContent = count > 99 ? "99+" : String(count);
		this.refs.cartCount.hidden = count === 0;
		this._syncBasketChip();
	}

	/** The play screen's basket count while the pointer is locked (ASK-177 §13a item 4): shown when locked and the basket holds ≥ 1. */
	_syncBasketChip() {
		const chip = this.root.querySelector("[data-runtime-basket-chip]"), n = this.cartService?.count || 0;
		if (!chip) return;
		chip.querySelector("[data-runtime-basket-n]").textContent = n > 99 ? "99+" : String(n);
		chip.setAttribute("aria-label", `Basket: ${n} item${n === 1 ? "" : "s"}`);
		chip.hidden = !document.pointerLockElement || n === 0;
	}

	/** K-COM item 2 — "Your orders" in the cart's footer, on a GG-hosted shop only (its ledger is the one with a door). */
	_myOrdersButton() {
		return this._shopIsGgHosted() ? `<button type="button" class="kapowx-store__cart-orders" data-runtime-my-orders>Your orders</button>` : "";
	}

	renderCartPanel() {
		const panel = this.refs.cartPanel;
		panel.classList.toggle("is-open", this.state.cartOpen);

		const lines = this.state.cart;
		const count = this.cartService.count;
		if (this.refs.cartTitle) this.refs.cartTitle.textContent = count ? `Your cart · ${count}` : "Your cart";
		const closed = this._checkoutClosed();
		// ASK-139 C2b — the stopper SAYS WHEN. Not a disabled button: a statement in the button's place.
		const stopper = closed
			? `<div class="kapowx-store__checkout-stopper" data-runtime-checkout-closed><p class="kapowx-store__checkout-stopper-title">Checkout opens at launch</p><p class="kapowx-store__checkout-stopper-note">Your cart is kept for when it does.</p></div>`
			: "";
		// ASK-159 §2.18u — the checkout line (the total, then the stopper or the pay button) lives in the panel's
		// PINNED footer, never at the end of the line list: the list is the part that scrolls, so the money action
		// is on screen at every orientation and line count (Shopify/Apple cart drawer: sticky summary + action).
		// The r58 prod smoke measured the stopper 8 px under a 343 px landscape view when it trailed the list.
		if (!lines.length) {
			this.refs.cartItems.innerHTML = `<p class="kapowx-store__cart-empty">Your cart is empty.</p>`;
			this.refs.cartSummary.innerHTML = `${stopper}${this._myOrdersButton()}`;
			return;
		}

		const sellerName = this._resolveSellerOfRecord().legalName;
		const cur = this.state.currencyByShop?.[this.state.activeProjectId] || "";
		const amountOf = (v) => { const n = Number(String(v ?? "").replace(/[^0-9.\-]/g, "")); return Number.isFinite(n) && String(v ?? "").trim() !== "" ? n : null; };
		const priceText = (v) => { const n = amountOf(v); return n == null ? "—" : (/^[0-9.\s]+$/.test(String(v).trim()) && cur ? `${cur} ${v}` : String(v)); };
		const rows = lines.map((item) => `<div class="kapowx-store__cart-row" data-cart-line="${escapeAttribute(item.kapowxProductId || item.id || "")}"><span class="kapowx-store__cart-title">${escapeHtml(item.title)}</span><span class="kapowx-store__cart-qty">${item.quantity} × ${escapeHtml(priceText(item.price))}</span><span class="kapowx-store__cart-seller">${escapeHtml(sellerName)}</span><button type="button" class="kapowx-store__cart-remove" data-runtime-cart-remove="${escapeAttribute(String(item.lineId ?? item.id ?? ""))}" aria-label="Remove ${escapeAttribute(item.title || "this item")} from the cart"${item.pending ? " disabled aria-busy=\"true\"" : ""}>Remove</button></div>`).join("");
		// The total is only stated when every line has an amount — a partial sum would be a wrong number.
		const amounts = lines.map((l) => amountOf(l.price));
		const total = amounts.every((a) => a != null)
			? amounts.reduce((t, a, i) => t + a * (lines[i].quantity || 1), 0) : null;
		const totalRow = total != null
			? `<div class="kapowx-store__cart-total" data-cart-total><span>Total</span><span>${escapeHtml(cur ? `${cur} ${total.toFixed(2)}` : total.toFixed(2))}</span></div>`
			: "";

		// M5 C9 — merchant identity disclosure on cart per eComm rule 3 + 7
		// (DECISION-030 §5.3 + §5.7). Disclosure copy reads from the
		// sellerOfRecord resolver — never hardcoded "[Merchant]" per
		// DECISION-031 §11 disclosure-binding. Customer sees identity +
		// responsibility allocation before initiating checkout.
		const sellerOfRecord = this._resolveSellerOfRecord();
		const disclosureBlock = `
			<div class="kapowx-store__cart-disclosure">
				<p class="kapowx-store__cart-disclosure-text">${escapeHtml(sellerOfRecord.disclosureText)}</p>
			</div>
		`;

		// Checkout button only renders when the active transport supports it
		// (i.e. a gateway is configured). Local/preview deploys hide it.
		const checkoutBtn = closed ? stopper : this.cartService.supportsCheckout
			? `<button class="kapowx-store__btn-checkout${this.state.checkoutBusy ? " is-busy" : ""}" data-runtime-cart-checkout ${this.state.checkoutBusy ? "disabled" : ""}>${this.state.checkoutBusy ? `<span class="kapowx-store__btn-spinner" aria-hidden="true"></span>Setting up secure checkout…` : "Checkout"}</button>`
			: "";

		// The seller-of-record disclosure follows the lines it describes (each row also names the seller, and the
		// checkout overlay repeats it before payment — eComm rules 3 + 7).
		this.refs.cartItems.innerHTML = `${rows}${disclosureBlock}`;
		this.refs.cartSummary.innerHTML = `${totalRow}${checkoutBtn}${this._myOrdersButton()}`;
	}

	// D-076 — the product panel in this markup is THE panel for every type (right side sheet ≥ 1024 px,
	// bottom sheet below). Its field order is the contract `probe-product-panel` asserts: media · title ·
	// badges · price · maker · scale · Add to Cart · action row · verb line.
	renderShell() {
		return `
			<div class="kapowx-store">
				<div class="kapowx-store__stage" data-runtime-stage></div>
				<div class="kapowx-store__vignette" aria-hidden="true"></div>

				<div class="kapowx-store__overlay" aria-hidden="true">
					<div class="kapowx-store__crosshair"></div>

					<div class="kapowx-store__topbar">
						<span data-runtime-stage-status></span>
					</div>

					<!-- "Click to play" prompt — visible whenever the pointer is
					     not locked and the runtime is active (no menus open).
					     Click anywhere on the canvas engages pointer-lock via
					     the capture-phase handler in stage.js; we then hide
					     the prompt via pointerlockchange. -->
					<div class="kapowx-store__engage" data-runtime-engage hidden>
						<div class="kapowx-store__engage-inner">
							<div class="kapowx-store__engage-title">Click to play</div>
							<div class="kapowx-store__engage-sub">Use <kbd>W</kbd><kbd>A</kbd><kbd>S</kbd><kbd>D</kbd> to walk, <kbd>E</kbd> to interact, <kbd>Esc</kbd> for the menu</div>
						</div>
					</div>

					<div class="kapowx-store__box-hint" data-runtime-box-hint hidden>
						<span data-runtime-box-hint-name></span>
						<span class="kapowx-store__box-hint-cta">Press <kbd>E</kbd> to open</span>
					</div>

					<div class="kapowx-store__teleport-screen" data-runtime-loader role="status" aria-live="polite" hidden>
						<div class="kapowx-store__teleport-inner">
							<div class="kapowx-store__threshold-art" data-threshold-art hidden></div>
							<div class="kapowx-store__teleport-label" data-threshold-name></div>
							<div class="kapowx-store__teleport-spinner"></div>
						</div>
					</div>
				</div>

				<div class="kapowx-store__controls-hint is-hidden" data-runtime-controls-hint aria-live="polite">
					<div class="kapowx-store__controls-hint-title">Controls</div>
					<div class="kapowx-store__controls-hint-grid">
						<div class="kapowx-store__controls-hint-keys"><kbd>W</kbd><kbd>A</kbd><kbd>S</kbd><kbd>D</kbd></div>
						<div class="kapowx-store__controls-hint-action">Move</div>
						<div class="kapowx-store__controls-hint-keys"><kbd>Space</kbd> <kbd>Q</kbd></div>
						<div class="kapowx-store__controls-hint-action">Up / Down</div>
						<div class="kapowx-store__controls-hint-keys"><kbd>Shift</kbd></div>
						<div class="kapowx-store__controls-hint-action">Run</div>
						<div class="kapowx-store__controls-hint-keys">Mouse</div>
						<div class="kapowx-store__controls-hint-action">Look around (Esc for menu)</div>
						<div class="kapowx-store__controls-hint-keys"><kbd>E</kbd></div>
						<div class="kapowx-store__controls-hint-action">Interact (boxes, comics, lids)</div>
						<div class="kapowx-store__controls-hint-keys"><kbd>R</kbd></div>
						<div class="kapowx-store__controls-hint-action">Put back / close</div>
						<div class="kapowx-store__controls-hint-keys"><kbd>F</kbd></div>
						<div class="kapowx-store__controls-hint-action">Flip cover</div>
					</div>
					<div class="kapowx-store__controls-hint-dismiss">Press any key to dismiss</div>
				</div>

				<!-- ASK-087 AAA HUD ruling: the floating "?" help button is retired — its content lives in
				     the Esc pause/settings surface (Settings → Controls). During pointer-lock the cursor is
				     captured, so floating buttons are visible-but-unclickable; the remaining chrome
				     (settings / cart) is hidden while locked by updateEngagePromptVisibility(). -->
				<button class="kapowx-store__settings-btn" data-runtime-settings-btn data-hud aria-label="Settings">${kxIcon("settings", "header")}</button>

				<div class="kapowx-presence-pill" data-runtime-presence-count data-hud role="status" aria-live="polite" hidden><svg class="kapowx-icon" viewBox="0 0 24 24" aria-hidden="true"><circle cx="9" cy="8" r="3"/><path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6"/><circle cx="17" cy="9" r="2.4"/><path d="M15.5 14.3c.5-.2 1-.3 1.5-.3 2.5 0 4.5 2 4.5 4.5"/></svg><span data-runtime-presence-n></span></div>
				<button type="button" class="kapowx-chat-tab" data-runtime-chat-tab data-hud aria-label="Party chat" aria-expanded="false" hidden><svg class="kapowx-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M4 5h16v11H9l-5 4z"/></svg><span class="kapowx-chat-tab__dot" data-chat-unread hidden></span></button>
				<button type="button" class="kapowx-invite-pill" data-runtime-invite data-hud aria-label="Invite a friend to meet you here" hidden><svg class="kapowx-icon" viewBox="0 0 24 24" aria-hidden="true"><circle cx="9" cy="8" r="3"/><path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6"/><path d="M18 8v6"/><path d="M15 11h6"/></svg><span data-runtime-invite-label>Invite</span></button>
				<button type="button" class="kapowx-report-pill" data-runtime-report data-hud aria-label="Report a problem"><svg class="kapowx-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 21V4"/><path d="M5 4h11l-2 4 2 4H5"/></svg><span>Report</span></button>

				<button class="kapowx-store__cart-btn" data-runtime-cart-toggle data-hud aria-label="Open cart">
					<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
						<path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
						<line x1="3" y1="6" x2="21" y2="6"/>
						<path d="M16 10a4 4 0 0 1-8 0"/>
					</svg>
					<span class="kapowx-store__cart-count" data-runtime-cart-count hidden>0</span>
				</button>
				<!-- ASK-177 §13a item 4 (Vanessa, ALPHA031): the basket's count stays on the play screen while the pointer is locked — a
				     STATUS chip in the HUD pills' grammar where the basket button stands (a control cannot be reached in lock, ASK-087);
				     out of lock (and always on a phone) the basket button itself carries the count and a tap opens it; Esc as before. -->
				<div class="kapowx-basket-chip" data-runtime-basket-chip role="status" aria-live="polite" hidden><svg class="kapowx-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/></svg><span data-runtime-basket-n>0</span></div>

				<!-- Connector line from comic centre to info card -->
				<svg class="kapowx-store__comic-connector" data-runtime-comic-connector aria-hidden="true">
					<line x1="0" y1="0" x2="0" y2="0" stroke="var(--gg-accent-border)" stroke-width="1" stroke-dasharray="4,3"/>
				</svg>

				<!-- Floating info card — positioned in JS next to the hero comic -->
				<div class="kapowx-store__comic-hud" data-runtime-comic-hud hidden>
					<div class="kapowx-store__comic-hud-title" data-runtime-hud-title></div>
					<div class="kapowx-store__comic-hud-price" data-runtime-hud-price></div>
				</div>

				<!-- Fixed action cluster — bottom right, appears when comic is selected -->
				<div class="kapowx-store__comic-actions" data-runtime-comic-actions hidden>
					<button class="kapowx-store__action-btn kapowx-store__action-btn--add" data-runtime-add-selected>
						<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
							<path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
							<line x1="3" y1="6" x2="21" y2="6"/>
							<path d="M16 10a4 4 0 0 1-8 0"/>
						</svg>
						Add to cart
					</button>
					<button class="kapowx-store__action-btn kapowx-store__action-btn--flip" data-runtime-flip-comic>
						<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
							<polyline points="16 3 20 7 16 11"/>
							<path d="M20 7H10a4 4 0 0 0-4 4v0"/>
							<polyline points="8 21 4 17 8 13"/>
							<path d="M4 17H14a4 4 0 0 0 4-4v0"/>
						</svg>
						Flip
					</button>
					<button class="kapowx-store__action-btn kapowx-store__action-btn--back" data-runtime-close-comic>
						<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
							<polyline points="9 14 4 9 9 4"/>
							<path d="M20 20v-7a4 4 0 0 0-4-4H4"/>
						</svg>
						Put back
					</button>
				</div>

				<div class="kapowx-store__cart-panel" data-runtime-cart-panel data-sheet>
					<div class="kapowx-store__cart-header">
						<h2 data-runtime-cart-title>Your cart</h2>
						<button class="kapowx-store__cart-close" data-runtime-cart-close aria-label="Close cart">${kxIcon("close")}</button>
					</div>
					<div class="kapowx-store__cart-items" data-runtime-cart-items></div>
					<div class="kapowx-store__cart-footer" data-runtime-cart-footer>
						<div class="kapowx-store__cart-summary" data-runtime-cart-summary></div>
						<button class="kapowx-store__btn-clear" data-runtime-clear-cart>Clear cart</button>
					</div>
				</div>

				<!-- Mount point for the embedded-checkout overlay. CheckoutOverlay
				     renders its full-screen modal here when the player clicks
				     the cart's Checkout button (iter-4 embedded path). -->
				<div data-runtime-checkout-mount hidden></div>

				<!-- Settings overlay (ESC or ⚙ to open) -->
				<div class="kapowx-store__settings-overlay" data-runtime-settings-overlay hidden>
					<div class="kapowx-store__settings-panel" data-runtime-settings-panel>
						<div class="kapowx-store__settings-header">
							<h2>Settings</h2>
							<button class="kapowx-store__settings-close" data-runtime-settings-close aria-label="Close settings">${kxIcon("close")}</button>
						</div>
						<div class="kapowx-store__settings-body">
							<section class="kapowx-store__settings-section">
								<div class="kapowx-store__settings-section-title">Audio</div>
								<div class="kapowx-store__settings-row">
									<span id="kx-audio-volume">Volume</span>
									<input class="kapowx-store__settings-slider" type="range" min="0" max="100" value="70" data-setting="master" aria-labelledby="kx-audio-volume">
								</div>
								<div class="kapowx-store__settings-row">
									<span>Ambience</span>
									<button class="kapowx-store__toggle" data-setting="ambient" data-on="true" aria-pressed="true" aria-label="Ambience">ON</button>
								</div>
								<div class="kapowx-store__settings-row">
									<span>Cues</span>
									<button class="kapowx-store__toggle" data-setting="cues" data-on="true" aria-pressed="true" aria-label="Cues — the sounds of what you and the people beside you do">ON</button>
								</div>
							</section>
							<section class="kapowx-store__settings-section" data-runtime-avatar-section>
								<div class="kapowx-store__settings-section-title">Avatar</div>
								<div class="kapowx-store__settings-row kapowx-store__settings-row--stack">
									<span>Figure</span>
									<div class="kapowx-store__avatar-figures" role="radiogroup" aria-label="Figure" data-runtime-avatar-figures></div>
								</div>
								<div class="kapowx-store__settings-row kapowx-store__settings-row--stack">
									<span data-runtime-avatar-tint-label>Colour</span>
									<div class="kapowx-store__avatar-tints" role="radiogroup" aria-label="Colour" data-runtime-avatar-tints></div>
								</div>
								<div class="kapowx-store__avatar-note" data-runtime-avatar-note></div>
							</section>
							<section class="kapowx-store__settings-section">
								<div class="kapowx-store__settings-section-title">Controls</div>
								<div class="kapowx-store__settings-controls-grid">
									<span>Move</span>          <span>W A S D</span>
									<span>Look</span>          <span>Click → mouse (Esc releases)</span>
									<span>Run</span>           <span>Shift</span>
									<span>Up / Down</span>     <span>Space / Q</span>
									<span>Browse comics</span> <span>Scroll / ← →</span>
									<span>Open box</span>      <span>Click lid</span>
									<span>Inspect comic</span> <span>Click comic</span>
									<span>Put back / ESC</span><span>Put back btn</span>
									<span>Settings</span>      <span>ESC</span>
								</div>
							</section>
							<section class="kapowx-store__settings-section"><div class="kapowx-store__settings-section-title">Help</div><div class="kapowx-store__settings-row"><span>Touch controls</span><button class="kapowx-store__signout-btn" data-runtime-touch-hint>Show</button></div><div class="kapowx-store__settings-row"><span>Credits &amp; licences</span><button class="kapowx-store__signout-btn" data-runtime-credits>Open</button></div></section>
							${this.config.onSignOut ? `
								<section class="kapowx-store__settings-section">
									<div class="kapowx-store__settings-section-title">Account</div>
									<div class="kapowx-store__settings-row">
										<span class="kapowx-store__settings-email">${this.config.customerEmail ? escapeHtml(this.config.customerEmail) : "Signed in"}</span>
										<button class="kapowx-store__signout-btn" data-runtime-signout>Sign out</button>
									</div>
								</section>
							` : ""}
						</div>
					</div>
				</div>

				<div class="kapowx-store__empty" data-runtime-empty hidden>
					<p>No scene available. Publish a scene from the authoring tool first.</p>
				</div>


				<div class="kapowx-store__focus-dim" aria-hidden="true"></div>
				<div class="kapowx-store__hero-panel" data-hero-panel data-sheet role="dialog" aria-label="Product">
					<div class="kapowx-store__hero-panel-header">
						<button class="kapowx-store__hero-panel-close" data-hero-panel-close aria-label="Put back">${kxIcon("close")}</button>
					</div>
					<div class="kapowx-store__hero-panel-cover-wrap" data-hero-panel-media>
						<img class="kapowx-store__hero-panel-cover" data-hero-panel-cover src="" alt="" hidden>
						<div class="kapowx-store__hero-panel-cover-empty" data-hero-panel-cover-empty>No image</div>
						<div class="kapowx-store__hero-panel-media-3d" data-hero-panel-media-3d hidden>${kxIcon("cube", "mark")}<span class="kapowx-store__hero-panel-360">360°</span></div>
					</div>
					<div class="kapowx-store__hero-panel-thumbs" data-hero-panel-thumbs hidden></div>
					<div class="kapowx-store__hero-panel-info">
						<div class="kapowx-store__hero-panel-title" data-hero-panel-title></div>
						<div class="kapowx-store__hero-panel-badges" data-hero-panel-badges></div>
						<div class="kapowx-store__hero-panel-price" data-hero-panel-price></div>
						<div class="kapowx-store__hero-panel-maker" data-hero-panel-maker hidden></div>
						<div class="kapowx-store__hero-panel-scale" data-hero-panel-scale hidden></div>
					</div>
					<div class="kapowx-store__hero-panel-footer">
						<button class="kapowx-store__hero-panel-btn kapowx-store__hero-panel-btn--add" data-hero-panel-add>
							<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
								<path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
								<line x1="3" y1="6" x2="21" y2="6"/>
								<path d="M16 10a4 4 0 0 1-8 0"/>
							</svg>
							Add to Cart <span class="kapowx-store__hero-key">B</span>
						</button>
						<div class="kapowx-store__hero-panel-actions" data-hero-panel-actions></div>
						<button type="button" class="kapowx-store__action-btn kapowx-party-show-this" data-party-show-this hidden><svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true"><circle cx="10" cy="10" r="7" fill="none" stroke="var(--kx-social-party)" stroke-width="3"/></svg>Show this</button>
						<p class="kapowx-store__hero-panel-checkout-note" data-hero-panel-checkout-note hidden>Checkout opens at launch · ${planeWord()}</p>
						<div class="kapowx-store__hero-panel-hint" data-hero-panel-hint aria-live="polite"></div>
					</div>
				</div>
			</div>
		`;
	}
}

// ASK-135 §5 — ONE icon vocabulary for the customer chrome: 1.5 px stroke,
// `currentColor`, 16 px in panels and 20 px in the HUD, all from the token
// sheet. This replaces the dingbats and emoji that used to draw controls
// (⚙ ✕ ✓ ✨ 🎉 ⏳), which rendered at a different weight, colour and baseline
// on every OS and could not follow a token.
const KX_ICON_PATHS = {
	close:    '<path d="M18 6 6 18"/><path d="m6 6 12 12"/>',
	settings: '<circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>',
	check:    '<path d="M20 6 9 17l-5-5"/>',
	refresh:  '<path d="M21 12a9 9 0 1 1-2.64-6.36"/><path d="M21 3v6h-6"/>',
	clock:    '<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>',
	cube:     '<path d="M12 2.5 3.5 7v10l8.5 4.5 8.5-4.5V7z"/><path d="M3.5 7 12 11.5 20.5 7"/><path d="M12 11.5v10"/>',
	galaxy:   '<circle cx="12" cy="12" r="9"/><path d="M12 3a9 9 0 0 0 0 18"/><circle cx="12" cy="12" r="2.5"/>',
};

/** ASK-139 F-139c — a sheet module loaded on first use. Calls made before it lands are kept in order
 *  and replayed, so `open()` from a tap is never lost to the chunk fetch. */
function lazySheet(load) {
	let api = null, pending = null;
	const call = (name) => (...args) => {
		if (api) return api[name]?.(...args);
		pending ||= load().then((a) => (api = a)).catch((err) => { pending = null; console.warn("[sheet] unavailable:", err?.message || err); });
		return pending.then(() => api?.[name]?.(...args));
	};
	// openPerson — ASK-172 §8 (Client 5b): the Report sheet's "this person" (report-a-person from the chat's person card)
	return { open: call("open"), close: call("close"), maybeShow: call("maybeShow"), openPerson: call("openPerson") };
}

/** Inline SVG for a chrome icon. `size`: "panel" (16) · "header" (20) · "mark" (40). */
function kxIcon(name, size = "panel", { label } = {}) {
	const d = KX_ICON_PATHS[name];
	if (!d) return "";
	const cls = `kapowx-icon${size === "panel" ? "" : ` kapowx-icon--${size}`}`;
	const a11y = label ? `role="img" aria-label="${escapeAttribute(label)}"` : 'aria-hidden="true"';
	return `<svg class="${cls}" viewBox="0 0 24 24" ${a11y}>${d}</svg>`;
}

function escapeHtml(value) {
	return String(value)
		.replaceAll("&", "&amp;")
		.replaceAll("<", "&lt;")
		.replaceAll(">", "&gt;")
		.replaceAll('"', "&quot;")
		.replaceAll("'", "&#39;");
}

function escapeAttribute(value) {
	return String(value || "").replaceAll('"', "&quot;").replaceAll("'", "&#39;");
}

// Normalises a product entry so both the legacy local-WP shape and the
// Galleria Commerce Gateway feed shape work without fixture-side changes.
//
// Local shape       : { id, type, title, price, frontImage, backImage, ... }
// Gateway feed shape: { kapowxProductId, sourceProductId, kapowxProductType,
//                       title, price, media: { front, back, spine, label }, ... }
//
// After normalisation every entry has BOTH `frontImage`/`backImage`/`type`
// (legacy fields the fixture builders read) AND the canonical structure.
function _normalizeProduct(p) {
	if (!p || typeof p !== "object") return p;
	const media = p.media || null;
	// Interior-pages support (Phase E "E Open" verb). GCG admin owns the
	// authoring surface (per dev's May 2026 sync — moved out of the WC
	// plugin to keep the connector contract platform-neutral). Wire shape
	// accepts both camelCase and snake_case so the normalize is robust to
	// whichever the gateway emits.
	const wireInteriorImages = p.interiorImages ?? p.interior_images ?? null;
	let interiorImages = Array.isArray(wireInteriorImages)
		? wireInteriorImages.map(_rewriteImageUrlForCors).filter(Boolean)
		: [];
	const wireHasInterior = p.hasInterior ?? p.has_interior ?? false;
	// Drop the length>0 gate so a hasInterior=true product without images
	// yet still surfaces the verb (stage.openInterior gracefully falls back
	// to parchment-coloured stub pages — see the `images.length < 2` branch
	// there). Lets GCG flip the verb on for a product mid-iteration before
	// shipping the real images.
	let hasInterior = wireHasInterior === true;
	// Demo override for in-browser verification of the page-flip animation
	// without waiting for GCG to author real interior images. Activate via
	// `?demo-interior=1` on the URL; stubs hasInterior=true + 6 image slots
	// per product (re-using its own front/back covers) so the flip cycles
	// through three "spreads". Front/back loop is intentionally repetitive
	// — the goal is to exercise the gesture, not show real content.
	if (typeof window !== "undefined" && window.__kapowxInteriorDemoMode) {
		hasInterior = true;
		const front = _rewriteImageUrlForCors(p.frontImage ?? p.media?.front ?? null);
		const back  = _rewriteImageUrlForCors(p.backImage  ?? p.media?.back  ?? null);
		if (front && back) interiorImages = [front, back, front, back, front, back];
	}
	return {
		...p,
		// Stable id: prefer canonical kapowxProductId, then sourceProductId, then legacy id.
		id:         p.id         ?? p.kapowxProductId ?? p.sourceProductId ?? null,
		// Type filter used by fixture acceptsProductTypes.
		type:       p.type       ?? p.kapowxProductType ?? null,
		// Image URLs the fixture builders read directly. CORS rewrite per
		// _rewriteImageUrlForCors — see helper for why.
		frontImage: _rewriteImageUrlForCors(p.frontImage ?? media?.front ?? null),
		backImage:  _rewriteImageUrlForCors(p.backImage  ?? media?.back  ?? null),
		spineImage: _rewriteImageUrlForCors(p.spineImage ?? media?.spine ?? null),
		// ASK-139 F-139aj — which side the book is bound on. The wire says "left" | "right"; a row
		// that carries nothing (every feed before GCG's field) is left, the Western default.
		binding:    bookBinding(p),
		// pagesImage (the page-edge face) MUST be CORS-rewritten like the others —
		// otherwise it rides through only on `...p` as the raw storage.googleapis URL,
		// CORS-taints, and the page-edge face renders blank (M7 WS1 multi-face).
		pagesImage: _rewriteImageUrlForCors(p.pagesImage ?? media?.pages ?? null),
		labelImage: _rewriteImageUrlForCors(p.labelImage ?? media?.label ?? null),
		hasInterior,
		interiorImages,
		// D-076 cl.4 — the stamped hero render of a library model, `{ png, webp }` on the feed line.
		heroImageUrl: _rewriteImageUrlForCors(p.heroImage?.webp || p.heroImage?.png),
		// ASK-142 R1 — GCG's cooked levels per face, `media.derived.<face> = { shelf, hero, w, h }`
		// (KTX2, immutable; `interior[]` index-aligned with interiorImages). Absent when nothing is
		// cooked — the stage then draws the raw face, never a blank one.
		derived: _normalizeDerivedMedia(media?.derived ?? p.derived ?? null),
	};
}

function _normalizeDerivedMedia(d) {
	if (!d || typeof d !== "object") return null;
	const level = (f) => (f && typeof f === "object" && (f.shelf || f.hero))
		? { shelf: _rewriteImageUrlForCors(f.shelf || null), hero: _rewriteImageUrlForCors(f.hero || null), w: f.w ?? null, h: f.h ?? null }
		: null;
	const out = {};
	for (const face of ["front", "back", "spine", "pages", "label"]) { const l = level(d[face]); if (l) out[face] = l; }
	if (Array.isArray(d.interior)) out.interior = d.interior.map(level);
	return Object.keys(out).length ? out : null;
}

// CORS workaround for the GCG image bucket (gcg-images-dev / gcg-images-prod).
// The bucket has CORS configured, but Google's edge cache was populated by
// pre-CORS-config requests that omitted Origin headers and the cached
// responses (24h TTL) lack ACAO — so Three.js's crossOrigin="anonymous"
// fetches taint the canvas and comics render with blank covers.
//
// The CNAME-style URL `https://<bucket>.storage.googleapis.com/<path>` has
// its own cache key, hits a fresh cache slot, and serves the correct
// Access-Control-Allow-Origin response. Rewriting client-side here is a
// 4-line fix that unblocks the runtime without GCG-side coordination.
// Remove this shim once the GCG side migrates the canonical image URL
// shape OR the original cache entries expire (whichever first).
function _rewriteImageUrlForCors(url) {
	if (!url || typeof url !== "string") return url;
	const match = url.match(/^https:\/\/storage\.googleapis\.com\/([^/]+)\/(.+)$/);
	if (!match) return url;
	return `https://${match[1]}.storage.googleapis.com/${match[2]}`;
}
